import {
  AcaoCapacidadeAssistente,
  AlternativaCanonica,
  AvaliacaoDaResposta,
  CampoDeFormulario,
  CapacidadeAssistente,
  CapacidadesAssistente,
  CampoPlanoAssistente,
  ConsolidacaoDoPlanejamento,
  ChartSpecV1,
  DefinicaoReexecutavelDoGrafico,
  DecisaoConversacional,
  EtapaPlanoAssistente,
  EstadoCanvas,
  EstadoDaTarefaDoPlanejamento,
  EstadoDoPlanejamento,
  GrupoDaTarefaDoPlanejamento,
  HandleDeArtefatoGrafico,
  FalhaDaPrevia,
  FonteMensagem,
  ItemDeHistorico,
  MensagemAssistente,
  ModoDeAberturaDeTela,
  ModosDeAcao,
  MotivoDeSuporte,
  OfertaDeSuporte,
  PlanejamentoDoAssistente,
  PlanoAssistente,
  PreviaDeMutacao,
  PaginaDeResolvedor,
  ResultadoParcial,
  ResumoDoPlanejamento,
  SolicitacaoDeParametros,
  ValorDeParametro,
  RespostaAssistente,
  ResolvedorPublicado,
  SelecaoDaAntecipacao,
  TarefaDoPlanejamento,
  TelaAfetadaPelaMutacao,
  TipoDoPlanejamento,
  TradutorAssistente,
  VotoDaAvaliacao
} from '../dominio/assistente.interface'
/**
 * O cliente transporta o snapshot da tela hospedeira produzido pela
 * apresentação. O tipo é removido na compilação.
 */
import { ContextoDeTelaEnviado } from '../apresentacao/canvas/consentimentoDeLeituraDaTela'
import { chaveDoNomeDaTela } from '../apresentacao/canvas/nomeDeTela'
import { tabelaSemCredenciais } from '../dominio/campoSensivel'
import { corrigirConcordancia } from '../apresentacao/conversa/seloDeFerramenta'
/**
 * E12 — só os TIPOS da conversa relida. A forma do dado tem uma definição só,
 * no módulo de apresentação que a consome; o cliente apenas transporta.
 */
import { ResumoDeConversa, TranscricaoDeConversa } from '../apresentacao/historico/cartaoDeConversa'
import { chaveDoTextoDeNavegacao } from '../apresentacao/conversa/textoDeNavegacao'
import { cartaoDeAcaoPendente } from '../apresentacao/conversa/cartaoDeAcaoPendente'
import { textoDaNavegacaoComReconhecimento } from '../dominio/reconhecimentoDeDados'
import { criarLog } from './logDoAssistente'
import { interacaoSemPergunta } from '../dominio/interacaoSemPergunta'
import type { ContextoDaAcao } from '../dominio/contextoDaAcao'

const logDoCliente = criarLog('assistantClient')

/**
 * Fachada única do Assistente: fala só com `/assistente/v2`. Sem negociação de
 * versão, sem fallback v1, sem delegação MCP — a sessão usa o mesmo bearer que
 * o frontend já possui depois de `POST /api/auth`.
 */
const PREFIXO_V2 = '/assistente/v2'
const ERRO_TIPO_RESPOSTA_ASSISTENTE_DESCONHECIDO = 'tipo_resposta_assistente_desconhecido'
const EVENTO_ENVELOPE_ASSISTENTE_DESCONHECIDO = 'assistente_envelope_desconhecido' as const
const ERRO_NAVEGACAO_ASSISTENTE_MALFORMADA = 'navegacao_assistente_malformada'
const ERRO_RESOLVEDOR_ASSISTENTE_NAO_PUBLICADO = 'resolvedor_assistente_nao_publicado'
/**
 * Registry fechado dos resolvedores que este cliente sabe consultar. Fechado
 * continua sendo a regra — nome fora daqui falha ANTES do transporte, sem
 * virar texto/ID nem tentar outra rota. O que mudou é que ele deixou de ser um
 * nome só: `listar_feriados` e `consultar_regioes` entraram quando o backend
 * os publicou, e sem isso `regiao_id`/`feriado_id` seguiam como texto livre.
 */
const RESOLVEDORES_PUBLICADOS: ReadonlySet<ResolvedorPublicado> = new Set<ResolvedorPublicado>([
  'listar_empregados',
  'listar_feriados',
  'consultar_regioes'
])

export interface RegistroDeEnvelopeAssistenteDesconhecido {
  evento: typeof EVENTO_ENVELOPE_ASSISTENTE_DESCONHECIDO
  tipo: string
}

export type RegistradorDeEnvelopeAssistenteDesconhecido = (
  registro: RegistroDeEnvelopeAssistenteDesconhecido
) => void

const registrarEnvelopeAssistenteDesconhecido: RegistradorDeEnvelopeAssistenteDesconhecido = registro => {
  // O objeto é deliberadamente mínimo: registra o tipo, nunca o envelope bruto.
  // eslint-disable-next-line no-console
  console.warn(registro)
  logDoCliente.warn('envelope de tipo desconhecido', registro)
}

const gerarId = (): string => typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`

/**
 * Recorte mínimo de `IServicoHttp` usado por este módulo — permite injetar um
 * duplo de teste sem inicializar `@/main` (Vue app, store, router reais).
 */
export interface ClienteHttpAssistente {
  get<T>(url: string, config?: { params?: Record<string, unknown> }): Promise<{ data: T }>
  post<T>(url: string, data?: unknown): Promise<{ data: T }>
  /**
   * Só a avaliação da resposta usa PUT (upsert: a última vence). Opcional
   * para os dublês que não exercitam essa rota; o axios real sempre tem.
   */
  put?<T>(url: string, data?: unknown): Promise<{ data: T }>
}

// ---------------------------------------------------------------------------
// DTOs de transporte (espelho fino de aplicacoes/backend/mcp/*, snake_case).
// ---------------------------------------------------------------------------

interface WireItemRotulado { rotulo: string, valor: string }
interface WireEfeitoPrevia extends WireItemRotulado { severidade: 'informacao' | 'atencao' | 'perigo' }
interface WireValorNomeado { nome: string, valor: string }
interface WireParcial { mensagem: string, aplicado: string[], falhou: string[] }
export interface WireFonteAutoritativa {
  tipo: string
  contexto: string
  capacidade: string
  versao_manifesto: string
  identificador: string
  versao: string
  consultado_em: string
  rotulo: string
  dados_ate?: string | null
  fuso?: string | null
  campos?: string[]
  filtros?: string[]
  agregacoes?: string[]
  transformacoes?: string[]
  identificador_rota?: string | null
  rota_autorizada?: string | null
  frescor: string
}
interface WireColunaTabela { chave: string, rotulo: string }
interface WireLinhaTabela { valores: WireValorNomeado[] }
interface WireCartao { rotulo: string, valor: string, tom: 'padrao' | 'sucesso' | 'atencao' }
interface WireOpcaoFormulario { rotulo: string, valor: string }
interface WireCampoFormulario {
  nome: string
  rotulo: string
  tipo: 'texto' | 'data' | 'selecao' | 'booleano' | 'numero' | 'referencia'
  obrigatorio: boolean
  placeholder?: string | null
  opcoes: WireOpcaoFormulario[]
  resolvedor?: string | null
}

/**
 * Prévia conversacional de mutação: o backend manda as DUAS mensagens que os
 * botões enviam no chat. O front não redige texto de aprovação — quem
 * interpreta o "sim" é o turno seguinte, e a frase precisa ser a que a
 * plataforma fixou.
 */
interface WireDecisaoConversacional {
  mensagem_de_aprovacao?: string | null
  mensagem_de_recusa?: string | null
}

function mapearDecisaoConversacional (
  decisao?: WireDecisaoConversacional | null
): DecisaoConversacional | undefined {
  const aprovar = (decisao?.mensagem_de_aprovacao || '').trim()
  const recusar = (decisao?.mensagem_de_recusa || '').trim()
  return aprovar && recusar ? { aprovar, recusar } : undefined
}

interface WireRespostaComFonte {
  estado_da_fonte: 'autoritativa' | 'sem_fonte_autoritativa' | 'nao_aplicavel'
  fontes: WireFonteAutoritativa[]
}

interface WireGraficoDaColecao {
  formato: 'chart_spec_v1'
  conteudo: ChartSpecV1
  definicao_reexecutavel?: DefinicaoReexecutavelDoGrafico
  estado_da_fonte: 'autoritativa'
  fontes: WireFonteAutoritativa[]
}

interface WireHandleDeArtefatoGrafico {
  artefato_id?: string
  expira_em?: string
}

type WireRespostaAssistente =
  | (WireRespostaComFonte & { tipo: 'texto', texto: string, factual: boolean })
  | { tipo: 'conversa', texto: string, opcoes?: string[] | null }
  | (WireRespostaComFonte & { tipo: 'tabela', titulo: string, escopo: string, colunas: WireColunaTabela[], linhas: WireLinhaTabela[], filtros: string[], texto?: string | null })
  | (WireRespostaComFonte & { tipo: 'cartoes', titulo: string, cartoes: WireCartao[], decisao_conversacional?: WireDecisaoConversacional | null, texto?: string | null })
  | (WireRespostaComFonte & WireHandleDeArtefatoGrafico & { versao_contrato: '2.0', tipo: 'grafico', formato: 'chart_spec_v1', conteudo: ChartSpecV1, definicao_reexecutavel?: DefinicaoReexecutavelDoGrafico, texto?: string | null })
  | (WireRespostaComFonte & WireHandleDeArtefatoGrafico & { versao_contrato: '2.0', tipo: 'graficos', formato: 'chart_collection_v1', conteudo: { graficos: WireGraficoDaColecao[] }, texto?: string | null })
  | { tipo: 'formulario', titulo: string, descricao: string, capacidade: string, campos: WireCampoFormulario[], valores: WireValorNomeado[] }
  | { tipo: 'navegacao', rotulo: string, identificador_rota: string, rota_autorizada: string, rascunho_opaco?: string | null, identificador_tela?: string | null, modo_de_abertura?: string | null, dados_reconhecidos?: WireItemRotulado[] | null, texto?: string | null }
  | (WireRespostaComFonte & {
    tipo: 'previa'
    preview_id: string
    digest: string
    capacidade: string
    alvos: WireItemRotulado[]
    efeitos: WireEfeitoPrevia[]
    quantidade_afetada: number
    reversivel: boolean
    expira_em: string
    entrada: WireValorNomeado[]
  })
  | (WireRespostaComFonte & { tipo: 'resultado', estado: 'concluida' | 'concluida_reutilizada' | 'previa_aguardando_confirmacao', resumo: string, detalhes: WireItemRotulado[], reversivel: boolean | null, parcial?: WireParcial, decisao_conversacional?: WireDecisaoConversacional | null, texto?: string | null })
  | { tipo: 'resultado_incerto', estado: 'em_execucao' | 'resultado_desconhecido', chave_idempotencia: string, mensagem: string, correlacao: string, parcial?: WireParcial }
  | { tipo: 'negacao', codigo: string, mensagem: string, alternativa?: string | null }
  | { tipo: 'erro', codigo: string, mensagem: string, repetivel: boolean, correlacao?: string }

interface WireRespostaInteracao {
  sessao_id: string
  interacao_id: string
  submissao_id: string
  revisao: number
  estado: 'processando' | 'concluida' | 'negada' | 'falhou'
  resposta: WireRespostaAssistente | null
}

interface WireEventoStream {
  cursor: number
  tipo: string
  payload: Record<string, unknown>
  criado_em: string
}

interface WirePaginaStream {
  sessao_id: string
  interacao_id: string
  cursor_recebido: number
  proximo_cursor: number
  estado_interacao: string
  revisao_sessao: number
  eventos: WireEventoStream[]
}

interface WireRespostaDaSelecao {
  versao_contrato: string
  sessao_id: string
  interacao_retomada_id: string
  estado: string
}

interface WireRespostaDeParametros {
  versao_contrato: string
  sessao_id: string
  interacao_retomada_id: string
  estado: string
}

interface WireOpcaoResolvedor {
  rotulo: string
  valor: string
}

interface WirePaginaResolvedor {
  versao_contrato: string
  resolvedor: ResolvedorPublicado
  opcoes: WireOpcaoResolvedor[]
  total: number
  pagina: number
  tamanho_pagina: number
  proxima_pagina: number | null
}

interface WireAvaliacaoDaResposta {
  voto: string
  comentario?: string | null
}

interface WireInteracaoDaSessao {
  id: string
  /** `null` na resposta consolidada de um plano: ninguém perguntou nada. */
  pergunta: string | null
  estado: string
  resposta: WireRespostaAssistente | null
  /** A avaliação 👍/👎 já registrada para o turno, ou `null`. */
  avaliacao?: WireAvaliacaoDaResposta | null
  mensagens_tipadas?: unknown[] | null
  /** `consolidacao_do_plano` na resposta consolidada; `null` num turno comum. */
  origem?: string | null
}

interface WireSessao {
  id: string
  estado: string
  revisao: number
  /**
   * G8 — a conversa corrente vem junto da sessão. O backend sempre mandou;
   * o cliente é que descartava, e por isso um F5 esvaziava a tela.
   */
  interacoes?: WireInteracaoDaSessao[] | null
  /**
   * Se o menu de opções avançadas aparece e com que modos ele começa. Vem da
   * sessão, e não de `/capacidades`, porque esta rota não cai junto com o
   * MCP. Ausente (agente antigo) é menu oculto.
   */
  opcoes_avancadas?: WireOpcoesAvancadas | null
}

interface WireOpcoesAvancadas {
  exibir?: unknown
  modos_padrao?: { tela?: unknown, sistema?: unknown } | null
}

/** O que a sessão diz sobre o menu de opções avançadas, já lido. */
export interface OpcoesAvancadasDaSessao {
  exibir: boolean
  modosPadrao: ModosDeAcao
}

/**
 * Leitura estreita: só `exibir: true` mostra o menu, e um modo padrão só
 * começa desligado com `false` explícito — os dois ligados é o padrão
 * pedido, e um campo torto não pode desligar ação nenhuma por acidente.
 */
export function lerOpcoesAvancadasDaSessao (bruta: unknown): OpcoesAvancadasDaSessao {
  const opcoes = bruta && typeof bruta === 'object' && !Array.isArray(bruta)
    ? bruta as WireOpcoesAvancadas
    : {}
  const modos = opcoes.modos_padrao && typeof opcoes.modos_padrao === 'object' ? opcoes.modos_padrao : {}
  return {
    exibir: opcoes.exibir === true,
    modosPadrao: { tela: modos.tela !== false, sistema: modos.sistema !== false }
  }
}

/**
 * Um turno já encerrado da conversa corrente, como o cliente o entrega.
 *
 * `resposta` sobe CRUA de propósito: quem a traduz é o mesmo mapeador que o
 * poll usa (`mapearRespostaAssistente`), e ele precisa do tradutor, que vive na
 * apresentação. Traduzir aqui exigiria uma segunda cópia da tradução — e duas
 * cópias divergem.
 */
export interface TurnoDaSessao {
  id: string
  /** `null` na resposta consolidada de um plano de consulta. */
  pergunta: string | null
  /** `consolidacao_do_plano` ou `null` (turno comum, ou agente antigo). */
  origem: string | null
  estado: string
  resposta: unknown
  /** Avaliação já gravada no turno; volta no F5 para o botão não "esquecer" o voto. */
  avaliacao?: AvaliacaoDaResposta | null
  mensagensTipadas: MensagemAssistente[]
}

export interface SessaoResolvida {
  sessionId: string
  revision: number
  turnos: TurnoDaSessao[]
  opcoesAvancadas: OpcoesAvancadasDaSessao
}

/**
 * A avaliação como a sessão a devolve. Leitura estreita: voto fora do
 * vocabulário some (o botão volta a ficar neutro), e comentário só existe no
 * `nao_curtiu` — a mesma regra que o backend aplica na gravação.
 */
export function lerAvaliacaoDaSessao (bruta: unknown): AvaliacaoDaResposta | null {
  if (!bruta || typeof bruta !== 'object' || Array.isArray(bruta)) return null
  const { voto, comentario } = bruta as Record<string, unknown>
  if (voto !== 'curtiu' && voto !== 'nao_curtiu') return null
  const texto = voto === 'nao_curtiu' && typeof comentario === 'string' && comentario.trim()
    ? comentario.trim()
    : null
  return { voto, comentario: texto, estado: 'registrada' }
}

/** Teto do comentário do 👎, o mesmo que o agente e o condominio aplicam. */
export const LIMITE_DO_COMENTARIO_DA_AVALIACAO = 2000

/**
 * O corpo do PUT da avaliação. O comentário só viaja no `nao_curtiu` e só
 * quando tem texto: o agente recusa (422) comentário no `curtiu`, e uma chave
 * vazia não diz nada que a ausência não diga.
 */
export function corpoDaAvaliacao (
  voto: VotoDaAvaliacao,
  comentario?: string | null
): { voto: VotoDaAvaliacao, comentario?: string } {
  const texto = typeof comentario === 'string'
    ? comentario.trim().slice(0, LIMITE_DO_COMENTARIO_DA_AVALIACAO)
    : ''
  return voto === 'nao_curtiu' && texto ? { voto, comentario: texto } : { voto }
}

function turnosDaSessao (sessao: WireSessao): TurnoDaSessao[] {
  return (sessao.interacoes || []).map(interacao => ({
    id: interacao.id,
    pergunta: typeof interacao.pergunta === 'string' ? interacao.pergunta : null,
    origem: typeof interacao.origem === 'string' ? interacao.origem : null,
    estado: interacao.estado,
    resposta: interacao.resposta,
    avaliacao: lerAvaliacaoDaSessao(interacao.avaliacao),
    mensagensTipadas: mensagensTipadasDoWire(interacao.mensagens_tipadas)
  }))
}

const LOCALES_DA_SOLICITACAO = ['pt-BR', 'en-US', 'es-CO'] as const

function solicitacaoDeParametrosDoPayload (payload: unknown): SolicitacaoDeParametros | undefined {
  if (!ehRegistro(payload)) return undefined
  const {
    solicitacao_id: solicitacaoId,
    mensagem_id: mensagemId,
    interacao_de_origem_id: interacaoDeOrigemId,
    capacidade,
    campos,
    traducoes,
    estado
  } = payload
  if (!ehTextoNaoVazio(solicitacaoId) || !ehTextoNaoVazio(mensagemId) ||
    !ehTextoNaoVazio(interacaoDeOrigemId) || !ehTextoNaoVazio(capacidade) ||
    !Array.isArray(campos) || !campos.length || !ehRegistro(traducoes) ||
    !['pendente', 'respondida', 'cancelada'].includes(String(estado))) return undefined

  const traducoesMapeadas = Object.fromEntries(LOCALES_DA_SOLICITACAO.map(locale => {
    const traducao = traducoes[locale]
    if (!ehRegistro(traducao) || !ehTextoNaoVazio(traducao.titulo) || !ehTextoNaoVazio(traducao.acao)) return [locale, null]
    return [locale, { titulo: traducao.titulo, acao: traducao.acao }]
  }))
  if (Object.values(traducoesMapeadas).some(traducao => traducao === null)) return undefined

  const camposMapeados = campos.map(campo => {
    if (!ehRegistro(campo) || !ehTextoNaoVazio(campo.nome) ||
      !ehTextoNaoVazio(campo.parametro) ||
      !['data', 'hora', 'texto', 'textarea', 'booleano', 'inteiro', 'selecao'].includes(String(campo.tipo_de_campo)) ||
      campo.obrigatorio !== true || !ehRegistro(campo.traducoes)) return undefined
    if (campo.limite !== undefined && (!Number.isInteger(campo.limite) || Number(campo.limite) <= 0)) return undefined
    if (campo.minimo !== undefined && !Number.isInteger(campo.minimo)) return undefined
    if (campo.maximo !== undefined && !Number.isInteger(campo.maximo)) return undefined
    if (typeof campo.minimo === 'number' && typeof campo.maximo === 'number' && campo.minimo > campo.maximo) return undefined
    if (campo.valor_atual !== undefined && !valorCompativelComCampo(campo.tipo_de_campo, campo.valor_atual)) return undefined
    let opcoes: SolicitacaoDeParametros['campos'][number]['opcoes']
    if (campo.tipo_de_campo === 'selecao') {
      if (!Array.isArray(campo.opcoes) || !campo.opcoes.length) return undefined
      const valoresDasOpcoes = new Set<string>()
      const opcoesMapeadas = campo.opcoes.map(opcao => {
        if (!ehRegistro(opcao) || !ehTextoNaoVazio(opcao.valor) ||
          valoresDasOpcoes.has(opcao.valor) || !ehRegistro(opcao.traducoes)) return undefined
        valoresDasOpcoes.add(opcao.valor)
        const traducoesDaOpcao = Object.fromEntries(LOCALES_DA_SOLICITACAO.map(locale => {
          const traducao = (opcao.traducoes as Record<string, unknown>)[locale]
          if (!ehRegistro(traducao) || !ehTextoNaoVazio(traducao.rotulo)) return [locale, null]
          return [locale, { rotulo: traducao.rotulo }]
        }))
        if (Object.values(traducoesDaOpcao).some(traducao => traducao === null)) return undefined
        return {
          valor: opcao.valor,
          traducoes: traducoesDaOpcao as NonNullable<SolicitacaoDeParametros['campos'][number]['opcoes']>[number]['traducoes']
        }
      })
      if (opcoesMapeadas.some(opcao => opcao === undefined)) return undefined
      opcoes = opcoesMapeadas as NonNullable<SolicitacaoDeParametros['campos'][number]['opcoes']>
      if (typeof campo.valor_atual === 'string' &&
        !opcoes.some(opcao => opcao.valor === campo.valor_atual)) return undefined
    } else if (campo.opcoes !== undefined) return undefined
    let visivelQuando: { campo: string, igualA: boolean } | undefined
    if (campo.visivel_quando !== undefined) {
      if (!ehRegistro(campo.visivel_quando) || !ehTextoNaoVazio(campo.visivel_quando.campo) ||
        typeof campo.visivel_quando.igual_a !== 'boolean') return undefined
      visivelQuando = { campo: campo.visivel_quando.campo, igualA: campo.visivel_quando.igual_a }
    }
    const traducoesDoCampo = Object.fromEntries(LOCALES_DA_SOLICITACAO.map(locale => {
      const traducao = (campo.traducoes as Record<string, unknown>)[locale]
      if (!ehRegistro(traducao) || !ehTextoNaoVazio(traducao.rotulo) || !ehTextoNaoVazio(traducao.placeholder)) return [locale, null]
      return [locale, { rotulo: traducao.rotulo, placeholder: traducao.placeholder }]
    }))
    if (Object.values(traducoesDoCampo).some(traducao => traducao === null)) return undefined
    return {
      nome: campo.nome,
      parametro: campo.parametro,
      tipo: campo.tipo_de_campo,
      obrigatorio: true,
      ...(typeof campo.limite === 'number' ? { limite: campo.limite } : {}),
      ...(typeof campo.minimo === 'number' ? { minimo: campo.minimo } : {}),
      ...(typeof campo.maximo === 'number' ? { maximo: campo.maximo } : {}),
      ...(opcoes ? { opcoes } : {}),
      ...(campo.valor_atual !== undefined ? { valorAtual: campo.valor_atual as ValorDeParametro } : {}),
      ...(visivelQuando ? { visivelQuando } : {}),
      traducoes: traducoesDoCampo
    }
  })
  if (camposMapeados.some(campo => campo === undefined)) return undefined
  const camposValidos = camposMapeados as SolicitacaoDeParametros['campos']
  const nomes = new Set<string>()
  const parametros = new Set<string>()
  for (const campo of camposValidos) {
    if (nomes.has(campo.nome) || parametros.has(campo.parametro)) return undefined
    if (campo.visivelQuando) {
      const controlador = camposValidos.find(anterior => anterior.nome === campo.visivelQuando?.campo)
      if (!controlador || controlador.tipo !== 'booleano' || !nomes.has(controlador.nome)) return undefined
    }
    nomes.add(campo.nome)
    parametros.add(campo.parametro)
  }

  return {
    solicitacaoId,
    mensagemId,
    interacaoDeOrigemId,
    capacidade,
    campos: camposValidos,
    traducoes: traducoesMapeadas as SolicitacaoDeParametros['traducoes'],
    estado: estado as SolicitacaoDeParametros['estado']
  }
}

function valorCompativelComCampo (tipo: unknown, valor: unknown): boolean {
  if (tipo === 'booleano') return typeof valor === 'boolean'
  if (tipo === 'inteiro') return Number.isInteger(valor)
  return typeof valor === 'string'
}

function mensagensTipadasDoWire (mensagens: unknown): MensagemAssistente[] {
  if (!Array.isArray(mensagens)) return []
  const respostasPorMensagem = new Map<string, {
    mensagemId: string
    valores: Record<string, ValorDeParametro>
  }>()
  mensagens.forEach(mensagem => {
    if (!ehRegistro(mensagem) || mensagem.papel !== 'pessoa' ||
      !ehTextoNaoVazio(mensagem.mensagem_id) || !ehTextoNaoVazio(mensagem.responde_a) ||
      !ehRegistro(mensagem.valores)) return
    const valores = Object.fromEntries(Object.entries(mensagem.valores).flatMap(([nome, valor]) => {
      if (!ehRegistro(valor) || !['string', 'boolean', 'number'].includes(typeof valor.bruto)) return []
      return [[nome, valor.bruto as ValorDeParametro]]
    }))
    if (Object.keys(valores).length) {
      respostasPorMensagem.set(mensagem.responde_a, { mensagemId: mensagem.mensagem_id, valores })
    }
  })
  return mensagens.flatMap<MensagemAssistente>(mensagem => {
    if (!ehRegistro(mensagem)) return []
    if (mensagem.papel === 'assistente') {
      const solicitacao = solicitacaoDeParametrosDoPayload(mensagem)
      if (!solicitacao) return []
      const resposta = respostasPorMensagem.get(solicitacao.mensagemId)
      const valoresPreenchidos = resposta
        ? Object.fromEntries(solicitacao.campos.flatMap(campo => {
          const valor = resposta.valores[campo.nome]
          const valorCompativel = valor !== undefined && valorCompativelComCampo(campo.tipo, valor) &&
            (campo.tipo !== 'selecao' || campo.opcoes?.some(opcao => opcao.valor === valor) === true)
          return valorCompativel
            ? [[campo.nome, valor]]
            : []
        }))
        : undefined
      return [{
        id: solicitacao.mensagemId,
        role: 'assistant' as const,
        kind: 'pending' as const,
        solicitacaoDeParametros: {
          ...solicitacao,
          ...(valoresPreenchidos && Object.keys(valoresPreenchidos).length ? { valoresPreenchidos } : {})
        }
      }]
    }
    if (mensagem.papel !== 'pessoa' || !ehTextoNaoVazio(mensagem.mensagem_id) || !ehRegistro(mensagem.valores)) return []
    const valores = Object.values(mensagem.valores).flatMap(valor => {
      if (!ehRegistro(valor) || !['string', 'boolean', 'number'].includes(typeof valor.bruto)) return []
      return [String(valor.bruto)]
    })
    return valores.length
      ? [{ id: mensagem.mensagem_id, role: 'user' as const, kind: 'text' as const, text: valores.join('\n') }]
      : []
  })
}

/**
 * Backend antigo ainda publica `sugestoes` (frases prontas para a pessoa
 * enviar). O Assistente não sugere a próxima mensagem, então o campo é
 * ignorado aqui em vez de virar estado.
 */
interface WireCapacidades {
  versao_contrato: string
  versao_catalogo: string
  revisao_catalogo: string
  acoes?: WireAcaoCapacidade[]
}

interface WireAcaoCapacidade {
  id: string
  acao_fechada: string
  familia: string
  modo: string
  metodo: string
  somente_leitura: boolean
  objetivo: string
  aliases: string[]
  efeito: string | null
  entradas: string[]
  resolvedores_oficiais: string[]
  precondicoes: string[]
  resultados: string[]
  referencias: string[]
  proximas_operacoes: string[]
  filtros_oficiais: string[]
  owner: string | null
  protocolo: string | null
  estado: string | null
  apresentacao: string | null
  evidencia: string[]
  frescor: string | null
  proveniencia: string | null
}

interface WireCampoPlano {
  nome: string
  valor: string | number | boolean | null
}

interface WireAcaoPlano {
  id: string
  ordem: number
  capacidade: string
  preview_id: string
  digest_previa: string
  depende_de: string[]
  alvos: WireCampoPlano[]
  valores_atuais: WireCampoPlano[]
  valores_propostos: WireCampoPlano[]
  efeitos: string[]
  quantidade_afetada: number
  riscos: string[]
  reversivel: boolean
  garantia_atomicidade: string
  falha_parcial: string
  expira_em: string
  estado: string
  tentativas: number
  resultado: Record<string, unknown> | null
  codigo_erro: string | null
}

interface WirePlano {
  id: string
  objetivo: string
  digest: string
  efeitos: string[]
  quantidade_afetada: number
  criada_em: string
  expira_em: string | null
  confirmada_em: string | null
  digest_confirmado: string | null
  estado: string
  resumo: string
  remocoes: string[]
  acoes: WireAcaoPlano[]
}

interface WireItemHistorico {
  id: string
  sessao_id: string
  /** `null` na resposta consolidada de um plano de consulta. */
  pergunta: string | null
  origem?: string | null
  estado: string
  criada_em: string
  concluida_em: string | null
  resposta: WireRespostaAssistente | null
}

interface WirePaginaHistorico {
  versao_contrato: string
  itens: WireItemHistorico[]
  proximo_cursor: string | null
}

/**
 * E12 — histórico de CONVERSAS. Superfície só-v2: é endpoint novo, e v1 existe
 * para não quebrar quem já chamava.
 */
interface WireResumoConversa {
  id: string
  estado: string
  criada_em: string
  atualizada_em: string
  fechada_em: string | null
  primeira_pergunta: string | null
  total_interacoes: number
}

interface WirePaginaConversas {
  versao_contrato: string
  itens: WireResumoConversa[]
  proximo_cursor: string | null
}

interface WireTurnoDaConversa {
  interacao_id: string
  pergunta: string | null
  origem?: string | null
  resposta: string | null
  estado: string
  criada_em: string
  concluida_em: string | null
}

interface WireTranscricaoConversa {
  versao_contrato: string
  id: string
  estado: string
  criada_em: string
  fechada_em: string | null
  turnos: WireTurnoDaConversa[]
}

interface WireRascunhoTranscricao {
  rascunho_id: string
  texto: string
  expira_em: string
}

interface WireTranscricaoConfirmada {
  texto: string
}

interface WireRespostaPrevia {
  preview_id: string
  digest: string
  capacidade: string
  alvos: Array<{ label: string, value: string }>
  efeitos: Array<{ label: string, value: string, severity?: string }>
  quantidade_afetada: number
  reversivel: boolean
  expira_em: string
  entrada: Record<string, string>
}

interface WireRespostaConfirmacao {
  estado: string
  resumo: string
  detalhes: Array<{ label: string, value: string }>
  /** R6 — opcional: só vem quando a mutação de fato alterou o dado. */
  tela_afetada?: { identificador?: unknown, rota?: unknown } | null
}

interface WireRespostaReconciliacao {
  estado: string
  resumo: string | null
  detalhes: Array<{ label: string, value: string }>
}

// ---------------------------------------------------------------------------
// Mapeamento de fonte autoritativa -> vocabulário de UI existente.
// ---------------------------------------------------------------------------

function origemDaFonte (tipo: string): 'empresa' | 'ajuda' | 'inferencia' {
  if (tipo === 'politica' || tipo === 'documento') return 'ajuda'
  if (tipo === 'calculo_oficial') return 'inferencia'
  return 'empresa'
}

function mapearFontes (fontes: WireFonteAutoritativa[]): FonteMensagem[] {
  return fontes.map(fonte => ({
    origin: origemDaFonte(fonte.tipo),
    label: fonte.rotulo,
    text: `${fonte.contexto} · ${fonte.capacidade}`,
    link: fonte.rota_autorizada ?? undefined,
    tipo: fonte.tipo,
    contexto: fonte.contexto,
    capacidade: fonte.capacidade,
    versao_manifesto: fonte.versao_manifesto,
    identificador: fonte.identificador,
    versao: fonte.versao,
    consultado_em: fonte.consultado_em,
    rotulo: fonte.rotulo,
    dados_ate: fonte.dados_ate,
    fuso: fonte.fuso,
    campos: fonte.campos,
    filtros: fonte.filtros,
    agregacoes: fonte.agregacoes,
    transformacoes: fonte.transformacoes,
    identificador_rota: fonte.identificador_rota,
    rota_autorizada: fonte.rota_autorizada,
    frescor: fonte.frescor
  }))
}

function mapearTipoCampo (tipo: WireCampoFormulario['tipo']): CampoDeFormulario['type'] {
  if (tipo === 'data') return 'date'
  if (tipo === 'numero') return 'number'
  if (tipo === 'texto') return 'text'
  return 'select'
}

type CampoDeFormularioMapeado = CampoDeFormulario & { tipo: WireCampoFormulario['tipo'] }

function mapearCampoFormulario (campo: WireCampoFormulario, t: TradutorAssistente): CampoDeFormularioMapeado {
  const tipo = mapearTipoCampo(campo.tipo)
  const opcoes = campo.opcoes.map(opcao => ({ label: opcao.rotulo, value: opcao.valor }))
  const opcoesBooleanas = [
    { label: t('assistentePontotel.formularios.sim'), value: 'true' },
    { label: t('assistentePontotel.formularios.nao'), value: 'false' }
  ]
  return {
    name: campo.nome,
    label: campo.rotulo,
    type: tipo,
    tipo: campo.tipo,
    required: campo.obrigatorio,
    placeholder: campo.placeholder ?? undefined,
    options: tipo === 'select'
      ? (campo.tipo === 'booleano' ? opcoesBooleanas : opcoes)
      : undefined,
    resolvedor: campo.resolvedor
  }
}

function mapearAcaoCapacidade (acao: WireAcaoCapacidade): AcaoCapacidadeAssistente {
  return {
    id: acao.id,
    acao_fechada: acao.acao_fechada,
    familia: acao.familia,
    modo: acao.modo,
    metodo: acao.metodo,
    somente_leitura: acao.somente_leitura,
    objetivo: acao.objetivo,
    aliases: acao.aliases,
    efeito: acao.efeito,
    entradas: acao.entradas,
    resolvedores_oficiais: acao.resolvedores_oficiais,
    precondicoes: acao.precondicoes,
    resultados: acao.resultados,
    referencias: acao.referencias,
    proximas_operacoes: acao.proximas_operacoes,
    filtros_oficiais: acao.filtros_oficiais,
    owner: acao.owner,
    protocolo: acao.protocolo,
    estado: acao.estado,
    apresentacao: acao.apresentacao,
    evidencia: acao.evidencia,
    frescor: acao.frescor,
    proveniencia: acao.proveniencia
  }
}

export function mapearCapacidades (resposta: WireCapacidades): CapacidadesAssistente {
  return {
    versao_contrato: resposta.versao_contrato,
    versao_catalogo: resposta.versao_catalogo,
    revisao_catalogo: resposta.revisao_catalogo,
    acoes: (resposta.acoes || []).map(mapearAcaoCapacidade)
  }
}

function mapearCampoPlano (campo: WireCampoPlano): CampoPlanoAssistente {
  return { nome: campo.nome, valor: campo.valor }
}

function mapearEtapaPlano (acao: WireAcaoPlano): EtapaPlanoAssistente {
  return {
    id: acao.id,
    ordem: acao.ordem,
    capacidade: acao.capacidade,
    preview_id: acao.preview_id,
    digest_previa: acao.digest_previa,
    depende_de: acao.depende_de,
    alvos: acao.alvos.map(mapearCampoPlano),
    valores_atuais: acao.valores_atuais.map(mapearCampoPlano),
    valores_propostos: acao.valores_propostos.map(mapearCampoPlano),
    efeitos: acao.efeitos,
    quantidade_afetada: acao.quantidade_afetada,
    riscos: acao.riscos,
    reversivel: acao.reversivel,
    garantia_atomicidade: acao.garantia_atomicidade,
    falha_parcial: acao.falha_parcial,
    expira_em: acao.expira_em,
    estado: acao.estado,
    tentativas: acao.tentativas,
    resultado: acao.resultado,
    codigo_erro: acao.codigo_erro
  }
}

export function mapearPlanoAssistente (resposta: WirePlano): PlanoAssistente {
  return {
    id: resposta.id,
    objetivo: resposta.objetivo,
    digest: resposta.digest,
    efeitos: resposta.efeitos,
    quantidade_afetada: resposta.quantidade_afetada,
    criada_em: resposta.criada_em,
    expira_em: resposta.expira_em,
    confirmada_em: resposta.confirmada_em,
    digest_confirmado: resposta.digest_confirmado,
    estado: resposta.estado,
    resumo: resposta.resumo,
    remocoes: resposta.remocoes,
    etapas: resposta.acoes.map(mapearEtapaPlano)
  }
}

function mapearSeveridadePrevia (severidade: 'informacao' | 'atencao' | 'perigo'): 'info' | 'warn' | 'danger' {
  if (severidade === 'perigo') return 'danger'
  if (severidade === 'atencao') return 'warn'
  return 'info'
}

/**
 * Traduz o `parcial` do backend para `ResultadoParcial`. Ausente ⇒
 * `undefined`: sem esse campo, o canvas não mostra banner de parcial — o
 * cliente não decide sozinho que uma resposta ficou incompleta.
 */
function adaptarParcial (parcial?: WireParcial): ResultadoParcial | undefined {
  if (!parcial) return undefined
  return {
    message: parcial.mensagem,
    applied: parcial.aplicado,
    failed: parcial.falhou
  }
}

function ehTextoNaoVazio (valor: unknown): valor is string {
  return typeof valor === 'string' && valor.trim().length > 0
}

function validarNavegacaoWire (resposta: Extract<WireRespostaAssistente, { tipo: 'navegacao' }>): void {
  const rascunhoValido = resposta.rascunho_opaco === undefined
    || resposta.rascunho_opaco === null
    || typeof resposta.rascunho_opaco === 'string'
  if (!ehTextoNaoVazio(resposta.rotulo)
    || !ehTextoNaoVazio(resposta.identificador_rota)
    || !ehTextoNaoVazio(resposta.rota_autorizada)
    || !rascunhoValido) {
    throw new Error(ERRO_NAVEGACAO_ASSISTENTE_MALFORMADA)
  }
}

/**
 * `painel` navega no router do host. Ausência ou valor desconhecido mantém
 * `handoff`, que oferece a navegação na mensagem.
 */
function normalizarModoDeAbertura (valor: unknown): ModoDeAberturaDeTela {
  return valor === 'painel' ? 'painel' : 'handoff'
}

function mapearPreviaEmbutida (resposta: Extract<WireRespostaAssistente, { tipo: 'previa' }>): PreviaDeMutacao {
  return {
    previewId: resposta.preview_id,
    digest: resposta.digest,
    capability: resposta.capacidade,
    targets: resposta.alvos.map(alvo => ({ label: alvo.rotulo, value: alvo.valor })),
    effects: resposta.efeitos.map(efeito => ({
      label: efeito.rotulo,
      value: efeito.valor,
      severity: mapearSeveridadePrevia(efeito.severidade)
    })),
    affectedCount: resposta.quantidade_afetada,
    reversible: resposta.reversivel,
    expiresAt: resposta.expira_em,
    input: Object.fromEntries(resposta.entrada.map(item => [item.nome, item.valor])),
    sourceState: resposta.estado_da_fonte,
    sources: resposta.fontes.length ? mapearFontes(resposta.fontes) : undefined
  }
}

const ERRO_GRAFICO_ASSISTENTE_MALFORMADO = 'grafico_assistente_malformado'
const TIPOS_DE_GRAFICO = new Set(['bar', 'line', 'pie'])
const FORMATOS_DE_VALOR = new Set(['numero', 'percentual', 'moeda', 'duracao'])
const CHAVES_DO_ENVELOPE_DE_GRAFICO = [
  'versao_contrato', 'tipo', 'formato', 'conteudo', 'estado_da_fonte', 'fontes'
]
const CHAVES_DO_ITEM_DE_GRAFICO = ['formato', 'conteudo', 'estado_da_fonte', 'fontes']
const CHAVES_DO_CHART_SPEC = [
  'tipo', 'titulo', 'rotulo_categoria', 'rotulo_valor', 'formato_valor',
  'codigo_moeda', 'unidade', 'categorias', 'series'
]
const CHAVES_OPCIONAIS_DO_CHART_SPEC = ['empilhado']

function ehRegistro (valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor)
}

function temExatamenteAsChaves (valor: Record<string, unknown>, chaves: string[]): boolean {
  const recebidas = Object.keys(valor).sort()
  const esperadas = [...chaves].sort()
  return recebidas.length === esperadas.length && recebidas.every((chave, indice) => chave === esperadas[indice])
}

function ehTextoOuNulo (valor: unknown): valor is string | null {
  return typeof valor === 'string' || valor === null
}

function ehTextoOpcionalLimitado (valor: unknown, maximo: number): boolean {
  return valor === null || (typeof valor === 'string' && Boolean(valor.trim()) && valor.length <= maximo)
}

function ehCategoriaDoGrafico (valor: unknown): boolean {
  return valor === null || typeof valor === 'string' || typeof valor === 'boolean' ||
    (typeof valor === 'number' && Number.isFinite(valor))
}

function temChavesOpcionais (
  valor: Record<string, unknown>,
  chavesBase: string[],
  chavesOpcionais: string[]
): boolean {
  const chaves = [...chavesBase, ...chavesOpcionais.filter(chave => valor[chave] !== undefined)]
  return temExatamenteAsChaves(valor, chaves)
}

function ehDataHoraIsoComFuso (valor: unknown): valor is string {
  if (typeof valor !== 'string') return false
  const partes = valor.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|([+-])(\d{2}):(\d{2}))$/
  )
  if (!partes) return false

  const [, anoTexto, mesTexto, diaTexto, horaTexto, minutoTexto, segundoTexto, , , fusoHoraTexto, fusoMinutoTexto] = partes
  const ano = Number(anoTexto)
  const mes = Number(mesTexto)
  const dia = Number(diaTexto)
  const hora = Number(horaTexto)
  const minuto = Number(minutoTexto)
  const segundo = Number(segundoTexto)
  const bissexto = ano % 4 === 0 && (ano % 100 !== 0 || ano % 400 === 0)
  const diasNoMes = [31, bissexto ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  const fusoHora = fusoHoraTexto === undefined ? 0 : Number(fusoHoraTexto)
  const fusoMinuto = fusoMinutoTexto === undefined ? 0 : Number(fusoMinutoTexto)

  return mes >= 1 && mes <= 12 && dia >= 1 && dia <= (diasNoMes[mes - 1] ?? 0) &&
    hora <= 23 && minuto <= 59 && segundo <= 59 &&
    fusoHora <= 14 && fusoMinuto <= 59 && (fusoHora < 14 || fusoMinuto === 0) &&
    !Number.isNaN(Date.parse(valor))
}

function validarHandleDeArtefatoGrafico (valor: Record<string, unknown>): HandleDeArtefatoGrafico {
  const artefatoId = valor.artefato_id
  const expiraEm = valor.expira_em
  if (artefatoId === undefined && expiraEm === undefined) return {}
  if (
    typeof artefatoId !== 'string' || !artefatoId.startsWith('agf_') ||
    !artefatoId.slice(4).trim() || artefatoId.length > 200 ||
    !ehDataHoraIsoComFuso(expiraEm)
  ) {
    throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
  }
  return { artefatoId, expiraEm }
}

function ehDataIsoEstrita (valor: unknown): valor is string {
  if (typeof valor !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(valor)) return false
  const data = new Date(`${valor}T00:00:00.000Z`)
  return !Number.isNaN(data.valueOf()) && data.toISOString().slice(0, 10) === valor
}

function validarFiltroPeriodo (filtro: unknown): boolean {
  if (!ehRegistro(filtro) ||
    !temExatamenteAsChaves(filtro, ['id', 'tipo', 'rotulo', 'padrao', 'binds']) ||
    filtro.id !== 'periodo' || filtro.tipo !== 'periodo' ||
    typeof filtro.rotulo !== 'string' || !filtro.rotulo.trim() || filtro.rotulo.length > 100 ||
    !ehRegistro(filtro.padrao) ||
    !temExatamenteAsChaves(filtro.padrao, ['inicio', 'fim']) ||
    !ehRegistro(filtro.binds) ||
    !temExatamenteAsChaves(filtro.binds, ['inicio', 'fim'])) return false

  const inicio = filtro.padrao.inicio
  const fim = filtro.padrao.fim
  const bindInicio = filtro.binds.inicio
  const bindFim = filtro.binds.fim
  return ehDataIsoEstrita(inicio) && ehDataIsoEstrita(fim) && inicio < fim &&
    typeof bindInicio === 'string' && /^[A-Za-z_][A-Za-z0-9_]*$/.test(bindInicio) &&
    typeof bindFim === 'string' && /^[A-Za-z_][A-Za-z0-9_]*$/.test(bindFim) &&
    bindInicio !== bindFim
}

function validarDefinicaoReexecutavel (valor: unknown): DefinicaoReexecutavelDoGrafico {
  const chavesDaVisualizacao = [
    'tipo', 'titulo', 'coluna_categoria', 'coluna_valor', 'formato_valor'
  ]
  const chavesOpcionaisDaVisualizacao = [
    'coluna_serie', 'rotulo_categoria', 'rotulo_valor', 'codigo_moeda', 'unidade', 'empilhado'
  ]
  if (!ehRegistro(valor) ||
    !temChavesOpcionais(valor, ['versao', 'descricao', 'datasets', 'sql', 'binds', 'limite', 'visualizacao'], ['filtro_periodo']) ||
    valor.versao !== 'definicao_consulta_grafico_v1' ||
    typeof valor.descricao !== 'string' || valor.descricao.length < 1 ||
    valor.descricao.length > 200 || !valor.descricao.trim() ||
    !Array.isArray(valor.datasets) || valor.datasets.length < 1 || valor.datasets.length > 5 ||
    !valor.datasets.every(dataset => typeof dataset === 'string') ||
    typeof valor.sql !== 'string' || valor.sql.length < 1 || valor.sql.length > 4000 ||
    !ehRegistro(valor.binds) ||
    !Object.values(valor.binds).every(bind => bind === null || typeof bind === 'string' ||
      typeof bind === 'boolean' || (typeof bind === 'number' && Number.isFinite(bind))) ||
    !Number.isInteger(valor.limite) || (valor.limite as number) < 1 || (valor.limite as number) > 501 ||
    (valor.filtro_periodo !== undefined && !validarFiltroPeriodo(valor.filtro_periodo)) ||
    !ehRegistro(valor.visualizacao)) {
    throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
  }
  const visualizacao = valor.visualizacao
  if (!temChavesOpcionais(visualizacao, chavesDaVisualizacao, chavesOpcionaisDaVisualizacao) ||
    !TIPOS_DE_GRAFICO.has(String(visualizacao.tipo)) ||
    !FORMATOS_DE_VALOR.has(String(visualizacao.formato_valor)) ||
    ['titulo', 'coluna_categoria', 'coluna_valor'].some(campo => typeof visualizacao[campo] !== 'string') ||
    chavesOpcionaisDaVisualizacao.filter(campo => campo !== 'empilhado').some(campo =>
      visualizacao[campo] !== undefined && !ehTextoOuNulo(visualizacao[campo])
    ) ||
    (visualizacao.empilhado !== undefined && (
      visualizacao.empilhado !== true || visualizacao.tipo !== 'bar' ||
      typeof visualizacao.coluna_serie !== 'string' || !visualizacao.coluna_serie.trim()
    )) ||
    (visualizacao.formato_valor === 'moeda' &&
      (typeof visualizacao.codigo_moeda !== 'string' || !/^[A-Z]{3}$/.test(visualizacao.codigo_moeda))) ||
    (visualizacao.formato_valor !== 'moeda' &&
      visualizacao.codigo_moeda !== undefined && visualizacao.codigo_moeda !== null)) {
    throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
  }
  return valor as unknown as DefinicaoReexecutavelDoGrafico
}

function ehFonteAutoritativaDoGrafico (valor: unknown): boolean {
  if (!ehRegistro(valor)) return false
  const textosObrigatorios = [
    'tipo', 'contexto', 'capacidade', 'versao_manifesto', 'identificador',
    'versao', 'consultado_em', 'rotulo', 'frescor'
  ]
  const listasObrigatorias = ['campos', 'filtros', 'agregacoes', 'transformacoes']
  return textosObrigatorios.every(campo => typeof valor[campo] === 'string' && Boolean((valor[campo] as string).trim())) &&
    listasObrigatorias.every(campo => Array.isArray(valor[campo]))
}

/**
 * O discriminante `grafico` e uma fronteira fechada: alem de rejeitar o wire
 * antigo, esta guarda impede que opcoes livres do ECharts atravessem a API.
 */
function validarChartSpec (spec: unknown): ChartSpecV1 {
  if (!ehRegistro(spec) || !temChavesOpcionais(spec, CHAVES_DO_CHART_SPEC, CHAVES_OPCIONAIS_DO_CHART_SPEC)) {
    throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
  }
  if (
    !TIPOS_DE_GRAFICO.has(String(spec.tipo)) || typeof spec.titulo !== 'string' ||
    !spec.titulo.trim() || spec.titulo.length > 200 ||
    !ehTextoOpcionalLimitado(spec.rotulo_categoria, 100) ||
    !ehTextoOpcionalLimitado(spec.rotulo_valor, 100) ||
    !FORMATOS_DE_VALOR.has(String(spec.formato_valor)) || !ehTextoOuNulo(spec.codigo_moeda) ||
    !ehTextoOpcionalLimitado(spec.unidade, 40) ||
    !Array.isArray(spec.categorias) || !Array.isArray(spec.series) ||
    spec.categorias.length > 100 || spec.series.length > 5 ||
    !spec.categorias.every(ehCategoriaDoGrafico)
  ) {
    throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
  }
  if (
    (spec.formato_valor === 'moeda' && (typeof spec.codigo_moeda !== 'string' || !/^[A-Z]{3}$/.test(spec.codigo_moeda))) ||
    (spec.formato_valor !== 'moeda' && spec.codigo_moeda !== null) ||
    (spec.empilhado !== undefined && (spec.empilhado !== true || spec.tipo !== 'bar')) ||
    (spec.tipo === 'pie' && spec.series.length > 1) ||
    ((spec.categorias.length === 0) !== (spec.series.length === 0))
  ) {
    throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
  }

  for (const serie of spec.series) {
    if (
      !ehRegistro(serie) || !temExatamenteAsChaves(serie, ['nome', 'valores']) ||
      typeof serie.nome !== 'string' || !serie.nome.trim() || serie.nome.length > 100 ||
      !Array.isArray(serie.valores) ||
      serie.valores.length !== spec.categorias.length || serie.valores.length > 100 ||
      !serie.valores.every(valor => valor === null || (typeof valor === 'number' && Number.isFinite(valor)))
    ) {
      throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
    }
  }

  return spec as unknown as ChartSpecV1
}

function chartSpecDoEnvelope (resposta: unknown): {
  spec: ChartSpecV1
  definicaoReexecutavel?: DefinicaoReexecutavelDoGrafico
} & HandleDeArtefatoGrafico {
  if (!ehRegistro(resposta) ||
    !temChavesOpcionais(resposta, CHAVES_DO_ENVELOPE_DE_GRAFICO, [
      'texto', 'definicao_reexecutavel', 'artefato_id', 'expira_em'
    ])) {
    throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
  }
  if (
    resposta.versao_contrato !== '2.0' || resposta.tipo !== 'grafico' ||
    resposta.formato !== 'chart_spec_v1' || resposta.estado_da_fonte !== 'autoritativa' ||
    !Array.isArray(resposta.fontes) || resposta.fontes.length === 0 ||
    !resposta.fontes.every(ehFonteAutoritativaDoGrafico) ||
    (resposta.texto !== undefined && !ehTextoOuNulo(resposta.texto))
  ) {
    throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
  }

  return {
    spec: validarChartSpec(resposta.conteudo),
    ...validarHandleDeArtefatoGrafico(resposta),
    ...(resposta.definicao_reexecutavel === undefined
      ? {}
      : { definicaoReexecutavel: validarDefinicaoReexecutavel(resposta.definicao_reexecutavel) })
  }
}

interface ColecaoDeGraficosValidada {
  charts: Extract<EstadoCanvas, { type: 'charts' }>['charts']
  sources: FonteMensagem[]
  texto?: string | null
}

function colecaoDeGraficosDoEnvelope (resposta: unknown): ColecaoDeGraficosValidada & HandleDeArtefatoGrafico {
  if (!ehRegistro(resposta) || !temChavesOpcionais(
    resposta,
    CHAVES_DO_ENVELOPE_DE_GRAFICO,
    ['texto', 'artefato_id', 'expira_em']
  )) {
    throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
  }
  if (
    resposta.versao_contrato !== '2.0' || resposta.tipo !== 'graficos' ||
    resposta.formato !== 'chart_collection_v1' || resposta.estado_da_fonte !== 'autoritativa' ||
    !Array.isArray(resposta.fontes) || resposta.fontes.length === 0 ||
    !resposta.fontes.every(ehFonteAutoritativaDoGrafico) ||
    (resposta.texto !== undefined && !ehTextoOuNulo(resposta.texto)) ||
    !ehRegistro(resposta.conteudo) || !temExatamenteAsChaves(resposta.conteudo, ['graficos'])
  ) {
    throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
  }

  const graficos = resposta.conteudo.graficos
  if (!Array.isArray(graficos) || graficos.length < 2 || graficos.length > 4) {
    throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
  }

  const charts = graficos.map(grafico => {
    if (
      !ehRegistro(grafico) ||
      !temChavesOpcionais(grafico, CHAVES_DO_ITEM_DE_GRAFICO, ['definicao_reexecutavel']) ||
      grafico.formato !== 'chart_spec_v1' || grafico.estado_da_fonte !== 'autoritativa' ||
      !Array.isArray(grafico.fontes) || grafico.fontes.length === 0 ||
      !grafico.fontes.every(ehFonteAutoritativaDoGrafico)
    ) {
      throw new Error(ERRO_GRAFICO_ASSISTENTE_MALFORMADO)
    }
    return {
      spec: validarChartSpec(grafico.conteudo),
      ...(grafico.definicao_reexecutavel === undefined
        ? {}
        : { definicaoReexecutavel: validarDefinicaoReexecutavel(grafico.definicao_reexecutavel) }),
      sourceState: 'autoritativa' as const,
      sources: mapearFontes(grafico.fontes as WireFonteAutoritativa[])
    }
  })

  return {
    charts,
    ...validarHandleDeArtefatoGrafico(resposta),
    sources: mapearFontes(resposta.fontes as WireFonteAutoritativa[]),
    texto: resposta.texto as string | null | undefined
  }
}

/**
 * O turno GRAVOU alguma coisa? O agente marca `dados_alterados: true` no
 * envelope quando uma ferramenta do turno devolveu `resultado` concluído —
 * qualquer que seja o envelope que a tela desenha (o turno que grava e depois
 * relê responde com a tabela). Leitura estreita: só `true` literal conta.
 */
export function lerDadosAlterados (respostaBruta: unknown): boolean {
  return (respostaBruta as { dados_alterados?: unknown } | null)?.dados_alterados === true
}

const MOTIVOS_DE_SUPORTE: readonly MotivoDeSuporte[] = ['pedido_explicito', 'fora_do_alcance', 'falha_do_sistema']

/**
 * Teto do resumo que segue para o suporte. O agente manda até 800; a folga é
 * para o que a pessoa acrescentar no cartão. O `maxlength` do campo não corta
 * valor inicial, então o corte do que chega do agente mora aqui.
 */
export const TAMANHO_MAXIMO_DO_RESUMO_DE_SUPORTE = 1000

/** A oferta lateral do envelope, ou `null` se ausente ou torta. Nunca lança. */
export function lerOfertaDeSuporte (respostaBruta: unknown): OfertaDeSuporte | null {
  const bruta = (respostaBruta as { oferta_de_suporte?: unknown } | null)?.oferta_de_suporte
  if (!bruta || typeof bruta !== 'object') return null
  const { motivo, resumo } = bruta as Record<string, unknown>
  if (typeof motivo !== 'string' || !MOTIVOS_DE_SUPORTE.includes(motivo as MotivoDeSuporte)) return null
  if (typeof resumo !== 'string' || !resumo.trim()) return null
  return { motivo: motivo as MotivoDeSuporte, resumo: resumo.trim().slice(0, TAMANHO_MAXIMO_DO_RESUMO_DE_SUPORTE), estado: 'oferecida' }
}

/**
 * A oferta mora na última bolha do assistente do turno — a que explica o
 * desfecho. Turno sem bolha ganha uma, curta, para o cartão ter onde ficar.
 */
function anexarOfertaDeSuporte (mapeada: RespostaAssistente, oferta: OfertaDeSuporte, t: TradutorAssistente): RespostaAssistente {
  const messages = [...mapeada.messages]
  const indice = messages.map(mensagem => mensagem.role).lastIndexOf('assistant')
  if (indice < 0) {
    messages.push({ id: gerarId(), role: 'assistant', kind: 'text', text: t('assistentePontotel.suporte.semTexto'), ofertaDeSuporte: oferta })
  } else {
    messages[indice] = { ...messages[indice], ofertaDeSuporte: oferta }
  }
  return { ...mapeada, messages }
}

// ---------------------------------------------------------------------------
// Planejamento — campo LATERAL do envelope (`planejamento`), como a oferta de
// suporte: acompanha qualquer `tipo` e nunca muda o desenho da prosa. O mesmo
// objeto volta no corpo do GET e do POST de execução do plano.
// ---------------------------------------------------------------------------

/** A visão do plano como o agente a publica (snake_case do wire). */
export interface WireTarefaDoPlanejamento {
  id: string
  ordem: number
  descricao: string
  estado: string
  tentativas?: number
  motivo?: string | null
  grupo?: {
    chave: string
    rotulo_da_acao: string
    entidade_tipo: string
    entidade_tipo_plural?: string | null
    entidade_genero?: string | null
  } | null
  entidade_rotulo?: string | null
}

export interface WireConsolidacaoDoPlanejamento {
  estado: string
  interacao_id?: string | null
  gerada_em?: string | null
}

export interface WirePlanejamento {
  id: string
  conversa_id: string
  versao: number
  titulo: string
  /** `execucao` | `consulta`; ausente em agente antigo. */
  tipo?: string | null
  /** `null` no plano de execução. */
  consolidacao?: WireConsolidacaoDoPlanejamento | null
  estado: string
  criado_em?: string | null
  atualizado_em?: string | null
  finalizado_em?: string | null
  resumo?: Record<string, number>
  tarefas: WireTarefaDoPlanejamento[]
}

export const ESTADOS_DO_PLANEJAMENTO: readonly EstadoDoPlanejamento[] = [
  'aguardando_confirmacao', 'em_execucao', 'concluido', 'concluido_com_falhas', 'cancelado'
]

export const ESTADOS_DA_TAREFA_DO_PLANEJAMENTO: readonly EstadoDaTarefaDoPlanejamento[] = [
  'aguardando', 'executando', 'executado', 'falha', 'interrompida_pelo_supervisor'
]

/** Estados em que o plano não muda mais: o acompanhamento para neles. */
export const ESTADOS_TERMINAIS_DO_PLANEJAMENTO: readonly EstadoDoPlanejamento[] = [
  'concluido', 'concluido_com_falhas', 'cancelado'
]

export function planejamentoTerminal (estado: EstadoDoPlanejamento): boolean {
  return ESTADOS_TERMINAIS_DO_PLANEJAMENTO.includes(estado)
}

const textoOuNulo = (valor: unknown): string | null => (
  typeof valor === 'string' && valor.trim() ? valor.trim() : null
)

/**
 * A consolidação, lida estreita. Só existe no plano de consulta; num plano de
 * consulta sem campo legível ela é `pendente` — a tela segue perguntando, e o
 * agente decide quando ela acaba. `pronta` sem `interacao_id` não tem o que
 * buscar e também vira `pendente`: a próxima leitura traz o id.
 */
function lerConsolidacao (tipo: TipoDoPlanejamento, bruta: unknown): ConsolidacaoDoPlanejamento | null {
  if (tipo !== 'consulta') return null
  const consolidacao = bruta && typeof bruta === 'object' && !Array.isArray(bruta)
    ? bruta as Record<string, unknown>
    : {}
  const interacaoId = textoOuNulo(consolidacao.interacao_id)
  const geradaEm = textoOuNulo(consolidacao.gerada_em)
  if (consolidacao.estado === 'pronta' && interacaoId) return { estado: 'pronta', interacaoId, geradaEm }
  if (consolidacao.estado === 'falhou') return { estado: 'falhou', interacaoId: null, geradaEm: null }
  return { estado: 'pendente', interacaoId: null, geradaEm: null }
}

/**
 * O plano de consulta terminou de rodar, mas a resposta consolidada ainda não
 * está pronta: a tela segue consultando o plano (§7.5, opção i). A
 * consolidação só começa depois do fim com resultado, então "em execução" não
 * entra aqui — esse caso o acompanhamento já cobre — e cancelado também não:
 * o agente o publica como `falhou`, e um `pendente` torto ali não pode virar
 * um GET a cada 3 s para sempre.
 */
export function planejamentoAguardaConsolidacao (planejamento: PlanejamentoDoAssistente): boolean {
  return planejamento.tipo === 'consulta' &&
    (planejamento.estado === 'concluido' || planejamento.estado === 'concluido_com_falhas') &&
    planejamento.consolidacao?.estado === 'pendente'
}

/** A interação da resposta consolidada, quando o agente já a registrou. */
export function interacaoDaConsolidacaoPronta (planejamento: PlanejamentoDoAssistente): string | null {
  if (planejamento.tipo !== 'consulta' || planejamento.consolidacao?.estado !== 'pronta') return null
  return planejamento.consolidacao.interacaoId
}

/**
 * O grupo só vale inteiro: sem a chave, o rótulo da ação ou o tipo de
 * entidade não há frase de síntese honesta, e a tarefa aparece sozinha. O
 * plural e o gênero têm o mesmo padrão que o agente aplica.
 */
function lerGrupoDaTarefa (bruto: unknown): GrupoDaTarefaDoPlanejamento | null {
  if (!bruto || typeof bruto !== 'object' || Array.isArray(bruto)) return null
  const grupo = bruto as Record<string, unknown>
  const chave = textoOuNulo(grupo.chave)
  const rotuloDaAcao = textoOuNulo(grupo.rotulo_da_acao)
  const entidadeTipo = textoOuNulo(grupo.entidade_tipo)
  if (!chave || !rotuloDaAcao || !entidadeTipo) return null
  return {
    chave,
    rotuloDaAcao,
    entidadeTipo,
    entidadeTipoPlural: textoOuNulo(grupo.entidade_tipo_plural) || `${entidadeTipo}s`,
    entidadeGenero: grupo.entidade_genero === 'feminino' ? 'feminino' : 'masculino'
  }
}

function lerTarefaDoPlanejamento (bruta: unknown): TarefaDoPlanejamento | null {
  if (!bruta || typeof bruta !== 'object' || Array.isArray(bruta)) return null
  const tarefa = bruta as Record<string, unknown>
  const id = textoOuNulo(tarefa.id)
  const descricao = textoOuNulo(tarefa.descricao)
  const estado = tarefa.estado as EstadoDaTarefaDoPlanejamento
  if (!id || !descricao || typeof tarefa.ordem !== 'number' || !Number.isFinite(tarefa.ordem)) return null
  // Estado fora do vocabulário some: inventar "aguardando" mentiria sobre o
  // que o supervisor fez com a tarefa.
  if (!ESTADOS_DA_TAREFA_DO_PLANEJAMENTO.includes(estado)) return null
  return {
    id,
    ordem: tarefa.ordem,
    descricao,
    estado,
    tentativas: typeof tarefa.tentativas === 'number' && tarefa.tentativas >= 0 ? tarefa.tentativas : 0,
    motivo: estado === 'falha' || estado === 'interrompida_pelo_supervisor' ? textoOuNulo(tarefa.motivo) : null,
    grupo: lerGrupoDaTarefa(tarefa.grupo),
    entidadeRotulo: textoOuNulo(tarefa.entidade_rotulo)
  }
}

/** O resumo é contado das tarefas lidas: é o que a tabela mostra, sem divergir. */
export function resumoDasTarefas (tarefas: ReadonlyArray<TarefaDoPlanejamento>): ResumoDoPlanejamento {
  const resumo: ResumoDoPlanejamento = {
    total: tarefas.length, aguardando: 0, executando: 0, executado: 0, falha: 0, interrompidaPeloSupervisor: 0
  }
  for (const tarefa of tarefas) {
    if (tarefa.estado === 'interrompida_pelo_supervisor') resumo.interrompidaPeloSupervisor += 1
    else resumo[tarefa.estado] += 1
  }
  return resumo
}

/**
 * A visão do plano a partir de um objeto que TEM o campo `planejamento` — o
 * envelope do turno ou o corpo do GET/POST do plano. `null` se ausente ou
 * torta. Nunca lança: plano ilegível não pode custar a prosa do turno.
 *
 * Leitura estreita de propósito: só os campos da visão entram. Se o agente um
 * dia vazar `ferramenta`, `acao` ou `argumentos`, eles param aqui.
 */
export function lerPlanejamento (respostaBruta: unknown): PlanejamentoDoAssistente | null {
  const bruto = (respostaBruta as { planejamento?: unknown } | null)?.planejamento
  if (!bruto || typeof bruto !== 'object' || Array.isArray(bruto)) return null
  const plano = bruto as Record<string, unknown>
  const id = textoOuNulo(plano.id)
  const estado = plano.estado as EstadoDoPlanejamento
  if (!id || !ESTADOS_DO_PLANEJAMENTO.includes(estado) || !Array.isArray(plano.tarefas)) return null
  const tarefas = plano.tarefas
    .map(lerTarefaDoPlanejamento)
    .filter((tarefa): tarefa is TarefaDoPlanejamento => tarefa !== null)
    .sort((a, b) => a.ordem - b.ordem)
  if (tarefas.length === 0) return null
  // Tipo fora do vocabulário é o plano de sempre: só `consulta` muda a regra.
  const tipo: TipoDoPlanejamento = plano.tipo === 'consulta' ? 'consulta' : 'execucao'
  return {
    id,
    conversaId: textoOuNulo(plano.conversa_id) || '',
    versao: typeof plano.versao === 'number' && plano.versao >= 1 ? plano.versao : 1,
    titulo: textoOuNulo(plano.titulo) || '',
    tipo,
    consolidacao: lerConsolidacao(tipo, plano.consolidacao),
    estado,
    criadoEm: textoOuNulo(plano.criado_em),
    atualizadoEm: textoOuNulo(plano.atualizado_em),
    finalizadoEm: textoOuNulo(plano.finalizado_em),
    resumo: resumoDasTarefas(tarefas),
    tarefas
  }
}

/**
 * O plano mora na última bolha do assistente do turno — a que pergunta se
 * está correto, ou a que diz que a execução começou. Turno sem bolha ganha
 * uma com o título do plano, para o cartão ter onde ficar.
 */
function anexarPlanejamento (mapeada: RespostaAssistente, planejamento: PlanejamentoDoAssistente): RespostaAssistente {
  const messages = [...mapeada.messages]
  const indice = messages.map(mensagem => mensagem.role).lastIndexOf('assistant')
  if (indice < 0) {
    messages.push({ id: gerarId(), role: 'assistant', kind: 'text', text: planejamento.titulo, planejamento })
  } else {
    messages[indice] = { ...messages[indice], planejamento }
  }
  return { ...mapeada, messages }
}

/**
 * Traduz a resposta discriminada (`tipo`) do contrato v2 para o vocabulário de
 * UI já existente. Mensagem + canvas nascem juntos porque o contrato os
 * expressa como uma única resposta, nunca dois.
 */
function mapearRespostaPorTipo (
  respostaBruta: unknown,
  t: TradutorAssistente,
  registrarEnvelopeDesconhecido: RegistradorDeEnvelopeAssistenteDesconhecido = registrarEnvelopeAssistenteDesconhecido
): RespostaAssistente {
  const resposta = respostaBruta as WireRespostaAssistente
  const tipo = (resposta as unknown as { tipo?: unknown } | null)?.tipo
  if (typeof tipo !== 'string') throw new Error(ERRO_TIPO_RESPOSTA_ASSISTENTE_DESCONHECIDO)

  const mensagemBase = (extras: Partial<MensagemAssistente> = {}): MensagemAssistente => ({
    id: gerarId(),
    role: 'assistant',
    kind: 'text',
    ...extras
  })

  switch (resposta.tipo) {
    case 'texto':
      return {
        messages: [mensagemBase({
          text: resposta.texto,
          badge: resposta.estado_da_fonte === 'autoritativa' ? t('assistentePontotel.badges.dadosEmpresa') : null,
          sourceState: resposta.estado_da_fonte,
          sources: resposta.fontes.length ? mapearFontes(resposta.fontes) : undefined
        })],
        canvas: { type: 'empty' }
      }

    // Prosa conversacional não-autoritativa: balão de chat puro, sem badge,
    // sem fontes e sem canvas — a ausência de proveniência é o contrato.
    case 'conversa':
      return {
        // E8 — a clarificação com botões (C15/C17). `opcoes` só existe quando
        // o runtime ofereceu escolha; conversa comum segue sem a chave, e a
        // bolha sem `opcoes` é byte a byte a de antes desta change.
        messages: [mensagemBase({
          text: resposta.texto,
          ...(resposta.opcoes?.length ? { opcoes: resposta.opcoes } : {})
        })],
        canvas: { type: 'empty' }
      }

    case 'tabela': {
      /*
       * N7 — a rodada 3 fotografou `Phoneclock password: 1234` no canvas, ao
       * lado do nome da pessoa. O catálogo publica a coluna e o mapeador
       * copiava. A supressão entra AQUI, na fronteira da wire, para valer
       * igual no canvas, no histórico e em qualquer consumidor futuro do
       * mesmo envelope — deixar isso na camada de desenho garantiria que a
       * próxima tela a renderizar tabela repetisse o vazamento.
       */
      const semCredenciais = tabelaSemCredenciais(
        resposta.colunas.map(coluna => ({ key: coluna.chave, label: coluna.rotulo })),
        resposta.linhas.map(linha => Object.fromEntries(linha.valores.map(valor => [valor.nome, valor.valor])))
      )
      /*
       * N10 — "1 registro(s) autorizado(s)" empurra a conjugação para o leitor
       * resolver, e `corrigirConcordancia` existe e é testada exatamente para
       * esta frase desde o QW6. Faltava, de novo, o call site.
       *
       * A correção entra aqui e não no `CanvasTable` porque o MESMO texto vai
       * para o histórico em `history.meta`: consertar só na tabela deixaria a
       * gaveta exibindo o "(s)" — o segundo canal esquecido, que é o defeito
       * que o N9 acabou de fechar noutro lugar.
       */
      // `escopo` e opcional na pratica (ha envelope sem ele), e
      // `corrigirConcordancia` recebe string: sem o `|| ''` o mapeador lancava
      // e o turno inteiro virava cartao de erro.
      const escopoLegivel = corrigirConcordancia(resposta.escopo || '')
      const canvas: EstadoCanvas = {
        type: 'table',
        title: resposta.titulo,
        scope: escopoLegivel,
        filters: resposta.filtros,
        columns: semCredenciais.colunas,
        rows: semCredenciais.linhas,
        sourceState: resposta.estado_da_fonte,
        sources: resposta.fontes.length ? mapearFontes(resposta.fontes) : undefined
      }
      /*
       * P0 (raio-x 04/09) — a tabela era MUDA por construção, igual à
       * navegação antes do P5: "A Carla faltou ontem?" terminava em
       * "Encontrei os dados de … — confira ao lado", três turnos seguidos,
       * sem nunca dizer sim ou não. O backend JÁ manda a prosa do modelo em
       * `texto` (apresentacao.py anexa em todo tipo que aceita texto);
       * quando ela vem, é ELA que a pessoa lê — a frase enlatada vira
       * fallback para backend antigo, não o padrão.
       */
      const prosaDaTabela = typeof resposta.texto === 'string' ? resposta.texto.trim() : ''
      return {
        messages: [mensagemBase({
          text: prosaDaTabela || t('assistentePontotel.conversa.respostaTabela', { titulo: resposta.titulo }),
          badge: t('assistentePontotel.badges.dadosEmpresa'),
          sourceState: resposta.estado_da_fonte,
          sources: mapearFontes(resposta.fontes)
        })],
        canvas,
        history: { kind: 'consulta', title: resposta.titulo, meta: escopoLegivel, snapshot: canvas }
      }
    }

    case 'cartoes': {
      /*
       * 23/09/2026 — o cartão da rede da prévia em prosa chegou sem `fontes`,
       * o `.length` levantou, e um turno com uma anulação JÁ GRAVADA virou
       * "Algo falhou… Nada foi alterado". Cartão é o único widget que a
       * PLATAFORMA também escreve (a rede, a prévia conversacional); ausência
       * de fonte ali é a verdade — zero fontes —, não motivo para derrubar
       * o turno.
       */
      const fontesDoEnvelope = Array.isArray(resposta.fontes) ? resposta.fontes : []
      const fontes = fontesDoEnvelope.length ? mapearFontes(fontesDoEnvelope) : undefined
      // Só vira botão quando as DUAS mensagens vêm preenchidas: um par pela
      // metade deixaria a pessoa com um botão que não faz o que promete.
      const decisaoConversacional = mapearDecisaoConversacional(resposta.decisao_conversacional)
      const canvas: EstadoCanvas = {
        type: 'cards',
        title: resposta.titulo,
        cartoes: resposta.cartoes,
        ...(decisaoConversacional ? { decisaoConversacional } : {}),
        ...(fontes ? { sourceState: resposta.estado_da_fonte, sources: fontes } : {})
      }
      // Mesmo P0 da tabela: quando o modelo respondeu em prosa, a bolha é a
      // prosa; a frase enlatada só cobre envelope sem `texto`.
      const prosaDosCartoes = typeof resposta.texto === 'string' ? resposta.texto.trim() : ''
      return {
        messages: [mensagemBase({
          text: prosaDosCartoes || t('assistentePontotel.conversa.respostaCartoes', { titulo: resposta.titulo }),
          // O selo é afirmação de proveniência, como no `texto`: o cartão
          // "Confirmação pendente" da plataforma não é dado da empresa.
          badge: resposta.estado_da_fonte === 'autoritativa' ? t('assistentePontotel.badges.dadosEmpresa') : null,
          sourceState: resposta.estado_da_fonte,
          sources: mapearFontes(fontesDoEnvelope)
        })],
        canvas,
        history: { kind: 'consulta', title: resposta.titulo, meta: resposta.titulo, snapshot: canvas }
      }
    }

    case 'grafico': {
      const { spec, definicaoReexecutavel, ...handle } = chartSpecDoEnvelope(respostaBruta)
      const prosaDoGrafico = typeof resposta.texto === 'string' ? resposta.texto.trim() : ''
      const canvas: EstadoCanvas = {
        type: 'chart',
        spec,
        ...handle,
        ...(definicaoReexecutavel ? { definicaoReexecutavel } : {}),
        sourceState: resposta.estado_da_fonte,
        sources: resposta.fontes.length ? mapearFontes(resposta.fontes) : undefined
      }
      return {
        messages: [mensagemBase({
          text: prosaDoGrafico || spec.titulo,
          badge: t('assistentePontotel.badges.dadosEmpresa'),
          sourceState: resposta.estado_da_fonte,
          sources: mapearFontes(resposta.fontes)
        })],
        canvas,
        history: {
          kind: 'consulta',
          title: spec.titulo,
          meta: spec.unidade || spec.rotulo_valor || '',
          snapshot: canvas
        }
      }
    }

    case 'graficos': {
      const colecao = colecaoDeGraficosDoEnvelope(respostaBruta)
      const titulo = colecao.charts.map(grafico => grafico.spec.titulo).join(' / ')
      const prosaDosGraficos = typeof colecao.texto === 'string' ? colecao.texto.trim() : ''
      const canvas: EstadoCanvas = colecao.artefatoId === undefined
        ? { type: 'charts', charts: colecao.charts }
        : {
            type: 'charts',
            charts: colecao.charts,
            artefatoId: colecao.artefatoId,
            expiraEm: colecao.expiraEm as string
          }
      return {
        messages: [mensagemBase({
          text: prosaDosGraficos || titulo,
          badge: t('assistentePontotel.badges.dadosEmpresa'),
          sourceState: 'autoritativa',
          sources: colecao.sources
        })],
        canvas,
        history: {
          kind: 'consulta',
          title: titulo,
          meta: colecao.charts.map(grafico => grafico.spec.unidade).filter(Boolean).join(' / '),
          snapshot: canvas
        }
      }
    }

    case 'formulario':
      return {
        // E3 — quando o formulário já está COMPLETO, a bolha ganha o cartão de
        // ação pendente. Incompleto, `cartaoDeAcaoPendente` devolve `null` e o
        // veículo continua sendo o `CanvasActionForm`: é lá que se digita o que
        // falta, e um "Confirmar" com campo vazio prometeria o que não cumpre.
        messages: [mensagemBase({
          text: t('assistentePontotel.conversa.prepararAcao'),
          badge: t('assistentePontotel.badges.acaoConfirmavel'),
          acaoPendente: cartaoDeAcaoPendente(resposta) || undefined
        })],
        canvas: {
          type: 'form',
          title: resposta.titulo,
          description: resposta.descricao,
          capability: resposta.capacidade,
          schema: resposta.campos.map(campo => mapearCampoFormulario(campo, t)),
          values: Object.fromEntries(resposta.valores.map(valor => [valor.nome, valor.valor]))
        }
      }

    case 'navegacao': {
      validarNavegacaoWire(resposta)
      /*
       * 5.3 — a bolha lia "Abri Abrir calendário de feriados ao lado." e
       * "Dados considerados: /holidays". Nenhum dos dois é defeito do backend:
       * o `rotulo` é copy de BOTÃO (imperativa, certa na oferta do handoff) e a
       * rota é vocabulário de sistema (R8). Quando a tela tem nome publicado, é
       * ele que a pessoa lê nos dois lugares; sem nome mapeado, o rótulo volta
       * a ser o melhor que temos — nunca o caminho cru.
       */
      const modoDeAbertura = normalizarModoDeAbertura(resposta.modo_de_abertura)
      const chaveDaTela = chaveDoNomeDaTela(resposta.rota_autorizada)
      const nomeDaTela = chaveDaTela ? String(t(chaveDaTela)) : null
      const nomeiaATela = modoDeAbertura === 'painel' && Boolean(nomeDaTela)
      /*
       * P5 — a navegação era MUDA por construção.
       *
       * O envelope não tinha campo de texto, então uma pergunta que caísse no
       * atalho de navegação recebia a tela e uma das três frases fixas ("Abri a
       * tela X ao lado.") — nunca a resposta. Relatado assim: "abriu a tela e
       * nada? era só responder".
       *
       * Agora o backend manda `texto` — orientação por destino, autoritativa.
       * Quando vem, é ELE que a pessoa lê: a frase fixa descreve o gesto do
       * agente, e o texto do dono descreve o assunto. Ausente, as três frases
       * seguem valendo, então backend antigo não regride.
       */
      const orientacao = typeof resposta.texto === 'string' ? resposta.texto.trim() : ''
      const frase = orientacao || t(
        chaveDoTextoDeNavegacao(modoDeAbertura, nomeiaATela),
        nomeiaATela ? { tela: nomeDaTela } : { rotulo: resposta.rotulo }
      )

      /*
       * Quando o `texto` vem do dono, ele é a bolha INTEIRA — não recebe a
       * frase de reconhecimento colada por cima.
       *
       * O produtor do backend já inclui a nota de reconhecimento quando há
       * `dados_reconhecidos` ("Anotei o que você disse; confira na tela antes
       * de salvar"). Colar `dadosReconhecidosParcial` em cima disso diria
       * "Anotei" duas vezes na mesma bolha — e as duas se contradizem: uma pede
       * conferência na tela, a outra afirma que o dado NÃO foi levado. Quem
       * sabe se levou é quem preenche, e é do lado de lá.
       *
       * Sem `texto`, a frase é a enlatada e o reconhecimento continua sendo o
       * único jeito de a pessoa ver o que foi entendido.
       */
      return {
        messages: [mensagemBase({
          text: orientacao ? frase : textoDaNavegacaoComReconhecimento(
            frase,
            resposta.dados_reconhecidos,
            t,
            resposta.rascunho_opaco
          ),
          considered: nomeDaTela || resposta.rotulo
        })],
        canvas: {
          type: 'handoff',
          rotulo: resposta.rotulo,
          identificador_rota: resposta.identificador_rota,
          rota_autorizada: resposta.rota_autorizada,
          rascunho_opaco: resposta.rascunho_opaco,
          // Sem isto o consumo do rascunho vai pela chave ERRADA: o
          // `identificador_rota` é o nome da CAPACIDADE
          // (`abrir_folha_de_ponto_do_empregado`), e o registro de contextos de
          // abertura é indexado pelo identificador da TELA
          // (`folha_de_ponto_do_empregado`). Medido em 20/09/2026: o backend
          // devolvia os dois campos e o front consumia com o primeiro, levando
          // 404 em `_tela_publicada_ou_404` — tela abria vazia, sem erro visível.
          identificador_tela: resposta.identificador_tela,
          modo_de_abertura: modoDeAbertura
        }
      }
    }

    case 'previa': {
      const preview = mapearPreviaEmbutida(resposta)
      return {
        messages: [mensagemBase({
          text: t('assistentePontotel.conversa.prepararAcao'),
          sourceState: resposta.estado_da_fonte,
          sources: resposta.fontes.length ? mapearFontes(resposta.fontes) : undefined
        })],
        canvas: {
          type: 'preview',
          preview,
          sourceState: resposta.estado_da_fonte,
          sources: resposta.fontes.length ? mapearFontes(resposta.fontes) : undefined
        }
      }
    }

    case 'resultado': {
      if (resposta.estado_da_fonte !== 'autoritativa') {
        // Sem prova de efeito/leitura confirmada pelo owner, o envelope V2
        // NÃO marca sucesso: apresenta o resultado como erro curado (falta de
        // prova autoritativa), sem causa inventada.
        return {
          messages: [mensagemBase({ kind: 'error', text: t('assistentePontotel.erro.semProva') })],
          canvas: { type: 'error', code: 'sem_fonte_autoritativa', message: t('assistentePontotel.erro.semProva'), retryable: false }
        }
      }
      const decisaoConversacional = mapearDecisaoConversacional(resposta.decisao_conversacional)
      const canvas: EstadoCanvas = {
        type: 'success',
        summary: resposta.resumo,
        details: resposta.detalhes.map(item => ({ label: item.rotulo, value: item.valor })),
        reversible: resposta.reversivel,
        state: resposta.estado,
        ...(decisaoConversacional ? { decisaoConversacional } : {}),
        sourceState: resposta.estado_da_fonte,
        sources: resposta.fontes.length ? mapearFontes(resposta.fontes) : undefined,
        partial: adaptarParcial(resposta.parcial)
      }
      /*
       * Defeito C (23/09/2026) — o mesmo P0 da tabela, esquecido aqui: o
       * modelo respondia "Feito: o dia 18/09 da Ana está verificado…" e a
       * bolha mostrava só o `resumo` do envelope ("A ação foi concluída.";
       * na prévia, a instrução ao modelo com "(preview_id + digest)"). O
       * agente já anexa a prosa em `texto`; o resumo fica como título do
       * canvas e do histórico, e como bolha só quando o modelo calou.
       */
      const prosaDoResultado = typeof resposta.texto === 'string' ? resposta.texto.trim() : ''
      return {
        messages: [mensagemBase({
          kind: 'done',
          text: prosaDoResultado || resposta.resumo,
          sourceState: resposta.estado_da_fonte,
          sources: resposta.fontes.length ? mapearFontes(resposta.fontes) : undefined
        })],
        canvas,
        history: {
          kind: 'acao',
          title: resposta.resumo,
          meta: resposta.reversivel === true ? t('assistentePontotel.historico.reversivel') : t('assistentePontotel.historico.irreversivel'),
          reversible: resposta.reversivel ?? undefined,
          snapshot: canvas
        }
      }
    }

    case 'resultado_incerto':
      return {
        messages: [mensagemBase({ kind: 'pending', text: resposta.mensagem })],
        canvas: {
          type: 'uncertain',
          idempotencyKey: resposta.chave_idempotencia,
          message: resposta.mensagem,
          state: resposta.estado,
          partial: adaptarParcial(resposta.parcial),
          ...(resposta.correlacao ? { correlationId: resposta.correlacao } : {})
        }
      }

    case 'negacao':
      return {
        // E9 — o `codigo` acompanha a BOLHA, não só o canvas: quem humaniza
        // a falha na conversa precisa da causa, e adivinhá-la pela prosa de
        // quem falhou foi exatamente o que enganou o C13.
        messages: [mensagemBase({ kind: 'denied', text: resposta.mensagem, codigo: resposta.codigo, alternativa: resposta.alternativa || undefined })],
        canvas: { type: 'denied', code: resposta.codigo, message: resposta.mensagem, alternative: resposta.alternativa || undefined }
      }

    case 'erro':
      return {
        messages: [mensagemBase({ kind: 'error', text: resposta.mensagem, codigo: resposta.codigo })],
        canvas: {
          type: 'error',
          code: resposta.codigo,
          message: resposta.mensagem,
          retryable: resposta.repetivel,
          ...(resposta.correlacao ? { correlationId: resposta.correlacao } : {})
        }
      }

    default: {
      try {
        registrarEnvelopeDesconhecido({
          evento: EVENTO_ENVELOPE_ASSISTENTE_DESCONHECIDO,
          tipo
        })
      } catch {
        // Diagnóstico nunca pode transformar a degradação curada em falha do turno.
      }
      return {
        messages: [mensagemBase({
          text: t('assistentePontotel.conversa.respostaIncompativel')
        })],
        canvas: { type: 'empty' }
      }
    }
  }
}

/**
 * Traduz a resposta discriminada (`tipo`) do contrato v2 para o vocabulário de
 * UI, mais a oferta de suporte que pode acompanhar qualquer `tipo`.
 */
export function mapearRespostaAssistente (
  respostaBruta: unknown,
  t: TradutorAssistente,
  registrarEnvelopeDesconhecido: RegistradorDeEnvelopeAssistenteDesconhecido = registrarEnvelopeAssistenteDesconhecido
): RespostaAssistente {
  const mapeada = mapearRespostaPorTipo(respostaBruta, t, registrarEnvelopeDesconhecido)
  const oferta = lerOfertaDeSuporte(respostaBruta)
  const comOferta = oferta ? anexarOfertaDeSuporte(mapeada, oferta, t) : mapeada
  const planejamento = lerPlanejamento(respostaBruta)
  return planejamento ? anexarPlanejamento(comOferta, planejamento) : comOferta
}

/**
 * O desfecho de um turno que o backend CONCLUIU, pronto para a tela.
 *
 * `mapearRespostaAssistente` é estrito de propósito — um widget torto é pior
 * que nenhum. Só que quem chamava deixava a exceção subir até o catch do
 * envio, e ali ela virava `erroGenerico`: "Algo falhou do nosso lado… Nada foi
 * alterado". Em 23/09/2026 foi assim que um turno com uma anulação JÁ GRAVADA
 * disse à pessoa que nada tinha mudado. O turno concluiu; o que falhou foi
 * esta tela ler o envelope — e isso não autoriza afirmar nada sobre o efeito.
 *
 * A saída honesta é a prosa do modelo, que viu o que as ferramentas fizeram.
 * Sem prosa, a frase curada de "esta tela não sabe exibir", a mesma do tipo
 * desconhecido — que não afirma nem sucesso nem ausência de efeito.
 */
export function mapearDesfechoDoTurno (respostaBruta: unknown, t: TradutorAssistente): RespostaAssistente {
  try {
    return mapearRespostaAssistente(respostaBruta, t)
  } catch (erro) {
    const envelope = (respostaBruta !== null && typeof respostaBruta === 'object' ? respostaBruta : {}) as Record<string, unknown>
    try {
      // O objeto é mínimo, como o do tipo desconhecido: nunca o envelope bruto.
      // eslint-disable-next-line no-console
      console.warn({
        evento: 'assistente_envelope_ilegivel',
        tipo: typeof envelope.tipo === 'string' ? envelope.tipo : typeof envelope.tipo,
        motivo: erro instanceof Error ? erro.message : String(erro)
      })
      logDoCliente.error('envelope ilegível', {
        erro,
        envelope: removerDefinicaoReexecutavelDoLog(envelope)
      })
    } catch {
      // Diagnóstico nunca pode custar a resposta.
    }
    const prosa = typeof envelope.texto === 'string' ? envelope.texto.trim() : ''
    const degradada: RespostaAssistente = {
      messages: [{
        id: gerarId(),
        role: 'assistant',
        kind: 'text',
        text: prosa || t('assistentePontotel.conversa.respostaIncompativel')
      }],
      canvas: { type: 'empty' }
    }
    // O plano é lateral e tem leitor próprio, que não lança: um widget torto
    // não leva junto o cartão do plano que a prosa pergunta se está correto.
    const planejamento = lerPlanejamento(respostaBruta)
    return planejamento ? anexarPlanejamento(degradada, planejamento) : degradada
  }
}

function removerDefinicaoReexecutavelDoLog (
  valor: unknown,
  vistos = new WeakSet<object>()
): unknown {
  if (Array.isArray(valor)) {
    if (vistos.has(valor)) return '[circular]'
    vistos.add(valor)
    return valor.map(item => removerDefinicaoReexecutavelDoLog(item, vistos))
  }
  if (!ehRegistro(valor)) return valor
  if (vistos.has(valor)) return '[circular]'
  vistos.add(valor)
  return Object.fromEntries(Object.entries(valor)
    .filter(([chave]) => chave !== 'definicao_reexecutavel' && chave !== 'sql')
    .map(([chave, item]) => [chave, removerDefinicaoReexecutavelDoLog(item, vistos)]))
}

// ---------------------------------------------------------------------------
// Histórico — item persistido reaproveita o mesmo mapeamento por `tipo` da
// interação viva: a diferença é só a origem do dado (página paginada por
// cursor, não sessão em andamento).
// ---------------------------------------------------------------------------

export interface PaginaDeConversas {
  contractVersion: string
  items: ResumoDeConversa[]
  nextCursor: string | null
}

export interface PaginaDeHistorico {
  contractVersion?: string
  items: ItemDeHistorico[]
  nextCursor: string | null
}

export function mapearItemHistorico (item: WireItemHistorico, t: TradutorAssistente): ItemDeHistorico {
  // Um item ilegível não pode derrubar a página inteira do histórico.
  const mapeada = item.resposta ? mapearDesfechoDoTurno(item.resposta, t) : null
  const derivado = mapeada?.history
  return {
    id: item.id,
    kind: derivado?.kind || 'consulta',
    // A consolidação de um plano não tem pergunta: o título vem da resposta
    // ou, sem ela, diz o que o item é — nunca uma linha vazia na gaveta.
    title: derivado?.title || (interacaoSemPergunta(item) ? '' : item.pergunta as string) ||
      t('assistentePontotel.historico.respostaConsolidada'),
    meta: derivado?.meta || t(`assistentePontotel.historico.estados.${item.estado}`),
    at: item.concluida_em || item.criada_em,
    actor: t('assistentePontotel.usuarioPadrao'),
    reversible: derivado?.reversible,
    snapshot: mapeada?.canvas,
    sessao_id: item.sessao_id,
    ...(interacaoSemPergunta(item) ? {} : { pergunta: item.pergunta as string }),
    estado: item.estado,
    criada_em: item.criada_em,
    concluida_em: item.concluida_em,
    resposta: mapeada
  }
}

/**
 * Junta uma nova página ao que já está exibido, sem duplicar itens que já
 * apareceram (ex.: um item otimista local que a página seguinte também traz).
 */
export function mesclarPaginaHistorico (atual: ItemDeHistorico[], nova: ItemDeHistorico[]): ItemDeHistorico[] {
  const idsExistentes = new Set(atual.map(item => item.id))
  return [...atual, ...nova.filter(item => !idsExistentes.has(item.id))]
}

/**
 * Recorte da falha HTTP como o axios a entrega ao chamador (`erro.response`).
 * Único lugar do módulo que sabe cavar dentro de um erro de transporte.
 */
function lerFalhaHttp (erro: unknown): { status?: number, dados?: Record<string, unknown> } {
  if (typeof erro !== 'object' || erro === null || !('response' in erro)) return {}
  const resposta = (erro as { response: unknown }).response
  if (typeof resposta !== 'object' || resposta === null) return {}
  const status = 'status' in resposta && typeof resposta.status === 'number' ? resposta.status : undefined
  const bruto = 'data' in resposta ? (resposta as { data: unknown }).data : undefined
  const dados = typeof bruto === 'object' && bruto !== null ? bruto as Record<string, unknown> : undefined
  return { status, dados }
}

export function codigoDaFalhaHttp (erro: unknown): string | null {
  const detalhe = detalheDaFalhaHttp(erro)
  const codigo = detalhe?.codigo
  return typeof codigo === 'string' && codigo.trim() !== '' ? codigo : null
}

/** O status HTTP da recusa, ou `null` quando nem resposta houve (transporte). */
export function statusDaFalhaHttp (erro: unknown): number | null {
  return lerFalhaHttp(erro).status ?? null
}

/**
 * O código da recusa do agente nas rotas do plano. Ele vem em `detail.codigo`
 * (HTTPException) ou, num corpo próprio, em `codigo` direto — os dois valem.
 */
export function codigoDaRecusaDoPlanejamento (erro: unknown): string | null {
  const doDetalhe = codigoDaFalhaHttp(erro)
  if (doDetalhe) return doDetalhe
  const codigo = lerFalhaHttp(erro).dados?.codigo
  return typeof codigo === 'string' && codigo.trim() !== '' ? codigo : null
}

function detalheDaFalhaHttp (erro: unknown): Record<string, unknown> | null {
  const { dados } = lerFalhaHttp(erro)
  const detalhe = dados?.detail
  if (typeof detalhe !== 'object' || detalhe === null || Array.isArray(detalhe)) return null
  return detalhe as Record<string, unknown>
}

const STATUS_DE_GATEWAY = new Set([502, 503, 504])

/**
 * 5xx que veio do sistema responsável — e não da infraestrutura no caminho.
 *
 * Rodada de personas na BR.SOLAR (alpha-arquitetos, 08/10/2026), P11/T6–T7: um
 * rollout derrubou o agente e o gateway respondeu 502/503 cru, sem o nosso
 * `detail`. O chat dizia "O sistema responsável pela ação não a concluiu" e não
 * oferecia a nova tentativa. 502/503/504 sem `detail` estruturado é o caminho
 * fora do ar: mensagem genérica e retentativa. Com `detail`, foi o backend que
 * respondeu — continua sendo falha do dono.
 */
export function falhaDoDono (erro: unknown): boolean {
  const { status } = lerFalhaHttp(erro)
  if (typeof status !== 'number' || status < 500) return false
  if (!STATUS_DE_GATEWAY.has(status)) return true
  return detalheDaFalhaHttp(erro) !== null
}

const TIPOS_DE_ALTERNATIVA_CANONICA = new Set(['tela', 'importacao', 'job'])

/**
 * A alternativa canônica publicada na recusa. O conjunto de `tipo` é FECHADO:
 * um tipo desconhecido some inteiro em vez de virar um caminho que ninguém
 * sabe percorrer — dizer "não dá" é melhor do que apontar para lugar nenhum.
 *
 * `rota` chega pronta do servidor e é lida com a mesma régua estreita das
 * outras rotas do módulo: só caminho local. Rota ausente ou torta não invalida
 * a alternativa — a frase continua valendo e é o que a pessoa lê; o que se
 * perde é só o botão.
 */
export function alternativaCanonicaDaFalha (erro: unknown): AlternativaCanonica | null {
  const bruta = detalheDaFalhaHttp(erro)?.alternativa_canonica
  if (typeof bruta !== 'object' || bruta === null) return null

  const { tipo, identificador, descricao, rota } = bruta as Record<string, unknown>
  if (typeof tipo !== 'string' || !TIPOS_DE_ALTERNATIVA_CANONICA.has(tipo)) return null
  if (typeof identificador !== 'string' || identificador.trim() === '') return null
  if (typeof descricao !== 'string' || descricao.trim() === '') return null

  const rotaLocal = typeof rota === 'string' && rota.startsWith('/') && !rota.startsWith('//')
    ? rota.trim()
    : null

  return {
    tipo: tipo as AlternativaCanonica['tipo'],
    identificador: identificador.trim(),
    descricao: descricao.trim(),
    rota: rotaLocal
  }
}

/**
 * A recusa da prévia lida como o servidor a escreveu.
 *
 * A frase da alternativa canônica é a mensagem — ela foi escrita para uma
 * pessoa e diz o que fazer, enquanto o texto curado genérico ("Algo falhou do
 * nosso lado") não diz nada. O genérico só entra quando não há alternativa.
 *
 * `retryable` tem três origens, nesta ordem: o campo do servidor, que é
 * autoridade; a presença de alternativa canônica, que marca recusa deliberada
 * de governança (repetir repete a recusa); e o 5xx do dono, que já não
 * prometia retentativa. Só sobra `true` para o que de fato pode passar numa
 * segunda tentativa.
 */
export function recusaDaPrevia (t: TradutorAssistente, erro: unknown): FalhaDaPrevia {
  const detalhe = detalheDaFalhaHttp(erro)
  const alternativa = alternativaCanonicaDaFalha(erro)
  const ownerFalhou = falhaDoDono(erro)
  const repetivelDoServidor = detalhe?.retryable
  const correlacao = detalhe?.correlation_id

  return {
    code: codigoDaFalhaHttp(erro) || 'assistente_indisponivel',
    message: alternativa?.descricao
      || t(ownerFalhou ? 'assistentePontotel.erro.owner' : 'assistentePontotel.erro.mensagem'),
    retryable: typeof repetivelDoServidor === 'boolean'
      ? repetivelDoServidor
      : !alternativa && !ownerFalhou,
    ...(typeof correlacao === 'string' && correlacao.trim() !== '' ? { correlationId: correlacao } : {}),
    ...(alternativa ? { alternative: alternativa } : {})
  }
}

/** Código nomeado de um conflito HTTP 409, quando o backend o publicou. */
export function codigoDoConflitoDeSessao (erro: unknown): string | null {
  return lerFalhaHttp(erro).status === 409 ? codigoDaFalhaHttp(erro) : null
}

/** Código do backend para revisão de sessão desatualizada (HTTP 409). */
export const CODIGO_CONFLITO_DE_REVISAO = 'session_revision_conflict'

/**
 * Verdadeiro só para o conflito de revisão otimista da sessão. Qualquer outro
 * 409 do backend (`session_busy`, `session_closed`, ...) é recusa legítima e
 * NÃO autoriza reenvio: quem consome isto trata conflito re-sincronizando,
 * nunca repetindo às cegas.
 */
export function ehConflitoDeRevisaoDaSessao (erro: unknown): boolean {
  return lerFalhaHttp(erro).status === 409 && codigoDaFalhaHttp(erro) === CODIGO_CONFLITO_DE_REVISAO
}

/**
 * Códigos com que o axios marca a requisição que saiu e não obteve resposta.
 * Os mesmos que `http.axios.ts` já considera repetíveis, mais `ERR_NETWORK`
 * (o rótulo do axios 1.x para "a rede não entregou") e `ERR_CANCELED`.
 */
const CODIGOS_DE_TRANSPORTE = new Set([
  'ERR_NETWORK',
  'ERR_CANCELED',
  'ECONNABORTED',
  'ENOTFOUND',
  'ETIMEDOUT'
])

/**
 * Verdadeiro só para falha de TRANSPORTE: a requisição saiu e nunca obteve
 * resposta HTTP. É o caso que não tem `status` nenhum — e é por isso que ele
 * precisa de nome próprio.
 *
 * Sem esta distinção, transporte caído se confunde com recusa do servidor: um
 * 5xx TEM status e já é classificado como falha do sistema responsável
 * (`erroGenerico`), enquanto `ERR_NETWORK` escorregava para o mesmo balde do
 * erro transitório. Quem consome isto usa a diferença para decidir se "tente
 * novamente" é verdade, nunca para silenciar erro.
 *
 * A checagem é deliberadamente estreita: exige a marca do axios (`code`
 * conhecido, ou o par `request` presente / `response` ausente). Um `Error`
 * qualquer — bug de programação, exceção de dentro do próprio cliente — NÃO é
 * transporte, e continua caindo onde caía.
 */
export function ehFalhaDeTransporte (erro: unknown): boolean {
  if (typeof erro !== 'object' || erro === null) return false
  if (lerFalhaHttp(erro).status !== undefined) return false

  const codigo = (erro as { code?: unknown }).code
  if (typeof codigo === 'string' && CODIGOS_DE_TRANSPORTE.has(codigo)) return true

  const resposta = 'response' in erro ? (erro as { response: unknown }).response : undefined
  return 'request' in erro && (resposta === undefined || resposta === null)
}

function erroGenerico (t: TradutorAssistente, erro: unknown): RespostaAssistente {
  const codigo = codigoDaFalhaHttp(erro) || 'assistente_indisponivel'

  // 5xx do owner (delegação) é rejeição protegida: o sistema responsável
  // recusou/errou a execução sem confirmar efeito. Mensagem curada — honesta,
  // sem causa inventada e sem prometer que uma retentativa resolve. Gateway
  // fora do ar (502/503/504 sem o nosso `detail`) não é do dono: ver
  // `falhaDoDono`.
  const ownerFalhou = falhaDoDono(erro)
  const mensagem = t(ownerFalhou ? 'assistentePontotel.erro.owner' : 'assistentePontotel.erro.mensagem')
  return {
    messages: [{ id: gerarId(), role: 'assistant', kind: 'error', text: mensagem }],
    canvas: { type: 'error', code: codigo, message: mensagem, retryable: !ownerFalhou }
  }
}

/**
 * A4 — o poll local desistiu, e ninguém sabe o desfecho.
 *
 * O texto anterior era o genérico "Não conseguimos concluir essa solicitação
 * agora", e o cartão vizinho afirmava "Nada foi alterado" — sobre uma
 * interação que o backend seguia processando. A UI não tem como saber isso, e
 * dizê-lo é a única mentira que uma superfície de mutação não pode contar.
 *
 * `retryable` continua `true`, e é verdade: a retentativa da mesma pergunta
 * reusa o `submissao_id` (ver `state.ts`), então ela retoma o pedido em vez de
 * abrir outro — o texto diz exatamente isso.
 */
export function erroTempoDeEsperaEsgotado (t: TradutorAssistente): RespostaAssistente {
  const mensagem = t('assistentePontotel.erro.tempoEsgotado')
  return {
    messages: [{ id: gerarId(), role: 'assistant', kind: 'error', text: mensagem }],
    canvas: { type: 'error', code: 'tempo_de_espera_esgotado', message: mensagem, retryable: true }
  }
}

/**
 * Desfecho de sessão que não volta sozinha: a revisão foi re-lida do backend e
 * o conflito continuou, ou a sessão que o front carregava não existe mais.
 * `retryable: false` porque "tente novamente" seria mentira — o que resolve é
 * começar outra conversa, e é isso que `recoverable` habilita na interface.
 */
export function erroSessaoTravada (t: TradutorAssistente): RespostaAssistente {
  const mensagem = t('assistentePontotel.erro.sessaoTravada')
  return {
    messages: [{ id: gerarId(), role: 'assistant', kind: 'error', text: mensagem }],
    canvas: { type: 'error', code: 'sessao_travada', message: mensagem, retryable: false, recoverable: true }
  }
}

function urlDoResolvedorPublicado (resolvedor: string): string | null {
  return RESOLVEDORES_PUBLICADOS.has(resolvedor as ResolvedorPublicado)
    ? `${PREFIXO_V2}/resolvedores/${resolvedor}`
    : null
}

/** Resposta do consumo de contexto de abertura (subrouter de rascunhos). */
export interface WireContextoDeAberturaConsumido {
  identificador_tela: string
  estado: 'posicionado' | 'expirado'
  motivo: string | null
  payload: Record<string, unknown> | null
}

export interface ResultadoDoPoll {
  resposta: WireRespostaAssistente | null
  estadoInteracao: string
  revisaoSessao: number
  selecao?: SelecaoDaAntecipacao
  solicitacaoDeParametros?: SolicitacaoDeParametros
}

type OpcoesDoPoll = {
  intervaloMs?: number
  maximoDeTentativas?: number
  /** Relógio injetável (testes); o padrão é `Date.now`. */
  agora?: () => number
}

/**
 * O agente perdeu o MCP e vai tentar de novo depois de uma espera (10 s,
 * crescendo com jitter até 60 s, por até ~7 min). O evento chega como passo
 * de progresso — o `payload.texto` já é a frase para a pessoa — e SEM `tipo`
 * no payload, então não encerra o poll.
 */
export const EVENTO_DE_RECONEXAO_DO_MCP = 'mcp_reconectando'

/** Folga além da espera anunciada: a tentativa seguinte também leva tempo. */
export const FOLGA_DA_RECONEXAO_DO_MCP_MS = 120_000

/**
 * Até quando o poll espera depois de um aviso de reconexão: a espera
 * anunciada mais a folga. Sem número legível, conta a maior espera da
 * política (60 s) — melhor esperar demais do que abandonar um turno que o
 * agente ainda vai retomar.
 */
export function prazoDaReconexaoDoMcp (payload: Record<string, unknown> | null | undefined, agoraMs: number): number {
  const bruta = Number(payload?.proxima_em_segundos)
  const espera = Number.isFinite(bruta) && bruta >= 0 ? Math.min(bruta, 60) : 60
  return agoraMs + espera * 1000 + FOLGA_DA_RECONEXAO_DO_MCP_MS
}

/**
 * O teto do poll é orçamento, não palpite.
 *
 * O teto anterior — 40 tentativas de 700ms — desistia em ~32s, e os turnos de
 * mutação medidos ao vivo levaram de 26s a 40s: a mesma pergunta falhava ou
 * passava conforme o relógio, e o backend continuava `processando` com o
 * cliente já tendo desistido. Erro por relógio é pior que erro por falha,
 * porque ensina que a feature é loteria.
 *
 * A janela agora cobre com folga o orçamento do runtime (90s), e o ritmo é
 * dividido: rápido no começo, onde estão os eventos de progresso que a pessoa
 * lê, e espaçado depois, onde só resta esperar. Quem passa `intervaloMs`
 * explicitamente (os testes) mantém o intervalo fixo — a curva é o DEFAULT.
 */
export const INTERVALO_INICIAL_DO_POLL_MS = 700
export const INTERVALO_ESPACADO_DO_POLL_MS = 2_000
export const TENTATIVAS_EM_RITMO_RAPIDO = 10
/*
 * 23/09/2026: 58 tentativas (~103s) cobriam UMA mutação, não o pacote. O
 * pedido "desfaz duas coisas: 1)… 2)…" — decomposto em lista, com uma prévia
 * por item e as correções de formato no caminho — levou 87s de modelo e o
 * cliente desistiu segundos antes da resposta chegar, mostrando "Algo falhou…
 * Nada foi alterado" sobre um turno que tinha prévia pronta. Pacote é o fluxo
 * que queremos incentivar; a janela agora cobre 5 minutos.
 */
export const MAXIMO_DE_TENTATIVAS_DO_POLL = 158
/** Turno com pacote de ações (lista decomposta, uma prévia por item) com folga. */
export const JANELA_MINIMA_DO_POLL_MS = 300_000

export function intervaloDoPoll (tentativa: number): number {
  return tentativa < TENTATIVAS_EM_RITMO_RAPIDO
    ? INTERVALO_INICIAL_DO_POLL_MS
    : INTERVALO_ESPACADO_DO_POLL_MS
}

/**
 * Quanto tempo o poll espera antes de desistir, sem contar a latência das
 * requisições — a soma das esperas ENTRE as tentativas. É o número que o teste
 * confronta com `JANELA_MINIMA_DO_POLL_MS`.
 */
export function janelaDoPoll (maximoDeTentativas: number = MAXIMO_DE_TENTATIVAS_DO_POLL): number {
  let total = 0
  for (let tentativa = 0; tentativa < maximoDeTentativas - 1; tentativa++) {
    total += intervaloDoPoll(tentativa)
  }
  return total
}

function selecaoDaAntecipacaoDoEvento (evento: WireEventoStream): SelecaoDaAntecipacao | undefined {
  if (evento.tipo !== 'selecao' || !ehRegistro(evento.payload)) return undefined

  const { selecao_id: selecaoId, interacao_de_origem_id: interacaoDeOrigemId, parametro, titulo, opcoes } = evento.payload
  if (!ehTextoNaoVazio(selecaoId) || !ehTextoNaoVazio(interacaoDeOrigemId) ||
    !ehTextoNaoVazio(parametro) || !ehTextoNaoVazio(titulo) || !Array.isArray(opcoes)) return undefined

  const opcoesMapeadas = opcoes.map(opcao => {
    if (!ehRegistro(opcao) || !ehTextoNaoVazio(opcao.opcao_id) || !ehTextoNaoVazio(opcao.titulo)) return undefined
    if (opcao.subtitulo !== undefined && opcao.subtitulo !== null && !ehTextoNaoVazio(opcao.subtitulo)) return undefined
    return {
      opcaoId: opcao.opcao_id,
      titulo: opcao.titulo,
      ...(typeof opcao.subtitulo === 'string' ? { subtitulo: opcao.subtitulo } : {})
    }
  })
  if (opcoesMapeadas.some(opcao => opcao === undefined)) return undefined

  return {
    selecaoId,
    interacaoDeOrigemId,
    parametro,
    titulo,
    opcoes: opcoesMapeadas as SelecaoDaAntecipacao['opcoes'],
    estado: 'pendente'
  }
}

export function solicitacaoDeParametrosDoEvento (evento: WireEventoStream): SolicitacaoDeParametros | undefined {
  if (evento.tipo !== 'solicitacao_de_parametros') return undefined
  return solicitacaoDeParametrosDoPayload({ ...evento.payload, estado: 'pendente' })
}

// ---------------------------------------------------------------------------
// Cliente HTTP
// ---------------------------------------------------------------------------

/** O recorte do resultado de preenchimento que o relato precisa. */
export interface ResultadoDoPreenchimentoParaORelato {
  aplicados: ReadonlyArray<{ campo: string, rotulo: string, valor: string }>
  recusados: ReadonlyArray<{ campo: string, valor: string, motivo: string }>
}

export class AssistenteHttpClient {
  /**
   * `http` é obrigatório e sempre injetado pelo chamador (ver
   * `apresentacao/state.ts`) — este módulo não conhece `@/main`, o que evita
   * o ciclo `assistantClient -> @/main -> store -> state -> assistantClient`
   * e permite testar a classe sem inicializar a aplicação Vue.
   */
  constructor (private readonly http: ClienteHttpAssistente) {}

  mensagemInicial (t: TradutorAssistente, nome: string): MensagemAssistente {
    return {
      id: gerarId(),
      role: 'assistant',
      kind: 'text',
      text: t('assistentePontotel.conversa.boasVindas', { nome }),
      badge: null,
      considered: t('assistentePontotel.conversa.escopoAtual')
    }
  }

  async obterSessaoAtiva (): Promise<SessaoResolvida | null> {
    const resposta = await this.http.get<WireSessao | null>(`${PREFIXO_V2}/sessoes/ativa`)
    if (!resposta.data) return null
    return {
      sessionId: resposta.data.id,
      revision: resposta.data.revisao,
      turnos: turnosDaSessao(resposta.data),
      opcoesAvancadas: lerOpcoesAvancadasDaSessao(resposta.data.opcoes_avancadas)
    }
  }

  /**
   * Resolve a conversa corrente do Contexto de Atuação. `nova` é o único dado
   * de intenção que o corpo carrega além do fuso: diz que a pessoa quer
   * recomeçar. Nenhuma autoridade sai daqui — tenant, empresa, perfil e escopo
   * continuam derivados pelo backend, que é quem encerra a conversa anterior e
   * abre a nova.
   *
   * Uma requisição, sem rede de proteção. Houve aqui um reenvio de
   * compatibilidade — o backend anterior ao campo `nova` recusava o corpo
   * INTEIRO com 422, e o cliente repetia sem o campo — que saiu quando o
   * backend passou a aceitá-lo (commit 6a8decb19dd). Ele era pior do que a
   * falha que evitava: o que voltava do reenvio era a conversa CORRENTE
   * ressincronizada, então "Nova conversa" devolvia a mesma conversa sem dizer
   * nada. Recusa que a pessoa vê vale mais que um recomeço que não recomeçou.
   */
  async resolverSessao (opcoes: { nova?: boolean } = {}): Promise<SessaoResolvida> {
    const fuso = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo'
    const corpo = opcoes.nova ? { fuso, nova: true } : { fuso }
    const resposta = await this.http.post<WireSessao>(`${PREFIXO_V2}/sessoes`, corpo)
    return {
      sessionId: resposta.data.id,
      revision: resposta.data.revisao,
      turnos: turnosDaSessao(resposta.data),
      opcoesAvancadas: lerOpcoesAvancadasDaSessao(resposta.data.opcoes_avancadas)
    }
  }

  async obterOuCriarSessao (): Promise<SessaoResolvida> {
    const ativa = await this.obterSessaoAtiva()
    return ativa || this.resolverSessao()
  }

  /**
   * Descobre o catálogo no endpoint público separado da sessão. O
   * `ServicoHttpAgentePtt` injeta a credencial vigente no transporte; esta
   * chamada não recebe nem envia sessão, papel, tenant ou permissões.
   */
  async listarCapacidadesDetalhadas (): Promise<CapacidadesAssistente> {
    const resposta = await this.http.get<WireCapacidades>(`${PREFIXO_V2}/capacidades`)
    return mapearCapacidades(resposta.data)
  }

  /**
   * O que a TELA fez com o rascunho, de volta para o turno que o pediu.
   *
   * O turno fecha antes de o campo receber o valor, então o modelo foi
   * proibido de afirmar o desfecho. Proibir tira a mentira e não põe o fato:
   * sem este relato, quem reabrisse a conversa lia "mandei preencher" e nunca
   * ficava sabendo o que entrou.
   *
   * Falha aqui é silêncio de propósito — o relato é um extra sobre um turno já
   * concluído, e derrubar a tela por causa dele trocaria um registro que falta
   * por um erro na cara da pessoa.
   */
  async registrarPreenchimento (
    sessaoId: string,
    interacaoId: string,
    resultado: ResultadoDoPreenchimentoParaORelato
  ): Promise<void> {
    const url = `${PREFIXO_V2}/sessoes/${encodeURIComponent(sessaoId)}/interacoes/${encodeURIComponent(interacaoId)}/preenchimento`
    try {
      await this.http.post(url, {
        aplicados: resultado.aplicados.map(campo => ({
          campo: campo.campo,
          rotulo: campo.rotulo,
          valor: campo.valor
        })),
        recusados: resultado.recusados.map(campo => ({
          campo: campo.campo,
          valor: campo.valor,
          motivo: campo.motivo
        }))
      })
    } catch {
      // Ver o comentário acima: o turno já terminou e a barra já contou.
    }
  }

  /**
   * 👍/👎 da resposta de um turno. PUT porque é upsert: há uma avaliação por
   * (pessoa, conversa, interação) e a última vence — trocar o voto não cria
   * outra. Quem é a pessoa vem do token; o corpo leva só o voto e, no
   * `nao_curtiu`, o comentário opcional.
   *
   * Diferente do relato de preenchimento, a falha SOBE: o botão precisa dizer
   * que o voto não ficou registrado, senão a pessoa acha que avaliou.
   */
  async avaliarResposta (
    sessaoId: string,
    interacaoId: string,
    avaliacao: { voto: VotoDaAvaliacao, comentario?: string | null }
  ): Promise<{ voto: VotoDaAvaliacao, comentario: string | null, registradaEm: string | null }> {
    if (typeof this.http.put !== 'function') throw new Error('cliente_http_sem_put')
    const url = `${PREFIXO_V2}/sessoes/${encodeURIComponent(sessaoId)}/interacoes/${encodeURIComponent(interacaoId)}/avaliacao`
    const corpo = corpoDaAvaliacao(avaliacao.voto, avaliacao.comentario)
    const resposta = await this.http.put<{ voto?: unknown, comentario?: unknown, registrada_em?: unknown }>(url, corpo)
    const dados = resposta.data || {}
    const voto = dados.voto === 'curtiu' || dados.voto === 'nao_curtiu' ? dados.voto : corpo.voto
    const comentario = typeof dados.comentario === 'string' ? dados.comentario : (corpo.comentario ?? null)
    return {
      voto,
      comentario: voto === 'nao_curtiu' ? comentario : null,
      registradaEm: typeof dados.registrada_em === 'string' ? dados.registrada_em : null
    }
  }

  /**
   * E4 — consome o rascunho de abertura que o produtor do backend publicou no
   * envelope de navegação (`rascunho_opaco`) e devolve o payload da tela.
   *
   * O handle NÃO é autoridade: quem posiciona é o backend, que rederiva tenant,
   * ator, contexto e sessão no consumo. Consumo é único e o TTL é curto — por
   * isso qualquer recusa (404, expirado, tela trocada) vira `null`, e a tela
   * abre vazia, que é exatamente o comportamento de hoje. Silêncio aqui é
   * degradação correta: a barra do painel é quem diz à pessoa o que houve.
   */
  async consumirContextoDeAbertura (
    identificadorTela: string,
    handle: string
  ): Promise<Record<string, unknown> | null> {
    const url = `${PREFIXO_V2}/rascunhos-canonicos/telas/${encodeURIComponent(identificadorTela)}/${encodeURIComponent(handle)}/consumir`
    try {
      const resposta = await this.http.post<WireContextoDeAberturaConsumido>(url)
      const dados = resposta.data
      if (!dados || dados.estado !== 'posicionado') return null
      if (dados.identificador_tela !== identificadorTela) return null
      const payload = dados.payload
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null
      return payload
    } catch {
      return null
    }
  }

  /**
   * A visão corrente do plano. Lê do agente, que lê do cache e, sem cache, do
   * back — e, a cada chamada com o plano em execução, renova a credencial com
   * que o supervisor trabalha. Corpo ilegível sobe como erro: o acompanhamento
   * decide o que fazer, e não pode tomar "nada" por "terminou".
   */
  async obterPlanejamento (sessaoId: string, planejamentoId: string): Promise<PlanejamentoDoAssistente> {
    const url = `${PREFIXO_V2}/sessoes/${encodeURIComponent(sessaoId)}/planejamentos/${encodeURIComponent(planejamentoId)}`
    const resposta = await this.http.get<{ planejamento?: WirePlanejamento }>(url)
    const planejamento = lerPlanejamento(resposta.data)
    if (!planejamento) throw new Error('planejamento_ilegivel')
    return planejamento
  }

  /**
   * O botão "Executar plano": a aprovação determinística, sem passar pelo
   * modelo. `versao` é a que a pessoa viu — se o plano foi replanejado em
   * outra aba, o agente recusa (409) em vez de executar outra lista.
   */
  async executarPlanejamento (
    sessaoId: string,
    planejamentoId: string,
    versao: number
  ): Promise<PlanejamentoDoAssistente> {
    const url = `${PREFIXO_V2}/sessoes/${encodeURIComponent(sessaoId)}/planejamentos/${encodeURIComponent(planejamentoId)}/execucao`
    const resposta = await this.http.post<{ planejamento?: WirePlanejamento }>(url, { versao })
    const planejamento = lerPlanejamento(resposta.data)
    if (!planejamento) throw new Error('planejamento_ilegivel')
    return planejamento
  }

  /**
   * A resposta de uma interação que JÁ terminou — hoje, a consolidada de um
   * plano de consulta. Uma leitura só da página de eventos (`cursor=0`), no
   * mesmo formato de um turno: o envelope vem no `payload` do evento de
   * resultado e desce CRU, para o mapeador de sempre traduzir. `null` quando a
   * página ainda não traz resultado (a próxima leitura do plano tenta de novo).
   */
  async obterRespostaDaInteracao (sessaoId: string, interacaoId: string): Promise<unknown | null> {
    const pagina = await this.listarEventos({ sessionId: sessaoId, interactionId: interacaoId, cursor: 0 })
    const resultado = (pagina?.eventos || []).find(evento => typeof evento?.payload?.tipo === 'string')
    return resultado ? resultado.payload : null
  }

  async obterPlano (planoId: string): Promise<PlanoAssistente> {
    const resposta = await this.http.get<WirePlano>(`${PREFIXO_V2}/planos/${planoId}`)
    return mapearPlanoAssistente(resposta.data)
  }

  /**
   * Consulta somente resolutores publicados pelo backend. O registry é
   * deliberadamente fechado: um nome desconhecido falha antes do transporte,
   * sem transformar a referência em texto/ID nem tentar outra rota.
   */
  async resolverOpcoes (
    resolvedor: string,
    opcoes: { busca?: string, pagina?: number, tamanhoPagina?: number } = {}
  ): Promise<PaginaDeResolvedor> {
    const url = urlDoResolvedorPublicado(resolvedor)
    if (!url) throw new Error(ERRO_RESOLVEDOR_ASSISTENTE_NAO_PUBLICADO)

    const params: Record<string, unknown> = {}
    /*
     * Busca vazia é AUSÊNCIA de busca, e é preciso dizer isso omitindo o
     * parâmetro: a rota declara `busca` com `min_length=1`, então `busca=`
     * volta 422. O picker abre com o campo em branco, então essa era a PRIMEIRA
     * chamada de toda montagem — medida ao vivo, 422 na abertura e 200 só
     * depois que a pessoa digitava. A lista inicial nunca vinha.
     */
    if (typeof opcoes.busca === 'string' && opcoes.busca.trim() !== '') params.busca = opcoes.busca
    if (opcoes.pagina !== undefined) params.pagina = opcoes.pagina
    if (opcoes.tamanhoPagina !== undefined) params.tamanho_pagina = opcoes.tamanhoPagina

    const resposta = await this.http.get<WirePaginaResolvedor>(url, { params })
    if (resposta.data.resolvedor !== resolvedor) {
      throw new Error(ERRO_RESOLVEDOR_ASSISTENTE_NAO_PUBLICADO)
    }
    return {
      versao_contrato: resposta.data.versao_contrato,
      resolvedor: resposta.data.resolvedor,
      opcoes: resposta.data.opcoes.map(opcao => ({ rotulo: opcao.rotulo, valor: opcao.valor })),
      total: resposta.data.total,
      pagina: resposta.data.pagina,
      tamanho_pagina: resposta.data.tamanho_pagina,
      proxima_pagina: resposta.data.proxima_pagina
    }
  }

  /**
   * `submissaoId` é opcional e existe para viabilizar o replay idempotente do
   * backend (`reserva.resposta_existente`): quem chama (ver
   * `apresentacao/state.ts`) guarda o `submissao_id` da tentativa corrente e
   * o reenvia em retentativas da MESMA pergunta — só quando não há um
   * `submissao_id` para reusar é que este método gera um novo. Sem isso, cada
   * retentativa do cliente cunhava um `submissao_id` novo e o backend nunca
   * reconhecia a resubmissão como a mesma operação.
   *
   * `contextoDeTela` é o snapshot da tela que a pessoa está vendo, colhido no
   * momento do envio: três listas (`estrutura`, `dados`, `acoes_e_campos`) sob
   * `versao: '3.0.0'`. Ele entra no corpo SÓ quando existe — ausência é
   * ausência de chave, nunca `contexto_de_tela: null`. Duas razões, e a segunda
   * é operacional: o turno sem painel tem que continuar idêntico ao de hoje, e
   * modelos `extra=forbid` do monólito recusam o corpo INTEIRO diante de uma
   * chave que não conhecem.
   *
   * O tipo é `ContextoDeTelaEnviado`, e não o que o observador colhe: só o
   * portão de consentimento produz esse tipo. Quem tiver em mãos um snapshot
   * cru não consegue mandá-lo para a rede sem passar por lá — ver
   * `apresentacao/canvas/consentimentoDeLeituraDaTela.ts`. Sem autorização o
   * envelope chega aqui já vazio de dados, carregando só a identidade da tela
   * e a marca `leitura_autorizada: false`.
   */
  async criarInteracao (params: {
    sessionId: string
    revision: number
    pergunta: string
    submissaoId?: string
    contextoDeTela?: ContextoDeTelaEnviado | null
    resultadoReferenciadoId?: string | null
    contextoDaAcao?: ContextoDaAcao
    suporteDisponivel?: boolean
    /** Só com o menu de opções avançadas exibido; ausente, o corpo é o de sempre. */
    modosDeAcao?: ModosDeAcao | null
  }): Promise<{
    sessionId: string
    interactionId: string
    submissionId: string
    revision: number
    estado: WireRespostaInteracao['estado']
    resposta: WireRespostaAssistente | null
  }> {
    const submissaoId = params.submissaoId || gerarId()
    const corpo: Record<string, unknown> = {
      submissao_id: submissaoId,
      revisao: params.revision,
      pergunta: params.pergunta
    }
    if (params.contextoDeTela) corpo.contexto_de_tela = params.contextoDeTela
    if (params.resultadoReferenciadoId) corpo.resultado_referenciado_id = params.resultadoReferenciadoId
    if (params.contextoDaAcao) corpo.contexto_da_acao = {
      tipo: params.contextoDaAcao.tipo,
      empregado_id: params.contextoDaAcao.empregadoId,
      nome_empregado: params.contextoDaAcao.empregadoNome,
      dia: params.contextoDaAcao.dia,
      contexto: params.contextoDaAcao.contexto
    }
    // Só quando verdadeiro: o campo ausente é o "não" que o agente já assume.
    if (params.suporteDisponivel === true) corpo.suporte_disponivel = true
    if (params.modosDeAcao) {
      corpo.modos_de_acao = { tela: params.modosDeAcao.tela === true, sistema: params.modosDeAcao.sistema === true }
    }
    const resposta = await this.http.post<WireRespostaInteracao>(
      `${PREFIXO_V2}/sessoes/${params.sessionId}/interacoes`,
      corpo
    )
    return {
      sessionId: resposta.data.sessao_id,
      interactionId: resposta.data.interacao_id,
      submissionId: resposta.data.submissao_id,
      revision: resposta.data.revisao,
      estado: resposta.data.estado,
      resposta: resposta.data.resposta
    }
  }

  async responderSelecao (params: {
    sessionId: string
    selecaoId: string
    interacaoDeOrigemId: string
    opcaoId: string
  }): Promise<{ sessionId: string, interactionId: string, estado: string }> {
    const url = `${PREFIXO_V2}/sessoes/${encodeURIComponent(params.sessionId)}/selecoes/${encodeURIComponent(params.selecaoId)}`
    const resposta = await this.http.post<WireRespostaDaSelecao>(url, {
      opcao_id: params.opcaoId,
      interacao_de_origem_id: params.interacaoDeOrigemId
    })
    return {
      sessionId: resposta.data.sessao_id,
      interactionId: resposta.data.interacao_retomada_id,
      estado: resposta.data.estado
    }
  }

  async responderSolicitacaoDeParametros (params: {
    sessionId: string
    solicitacaoId: string
    valores: Record<string, ValorDeParametro>
  }): Promise<{ sessionId: string, interactionId: string, estado: string }> {
    const url = `${PREFIXO_V2}/sessoes/${encodeURIComponent(params.sessionId)}/solicitacoes-de-parametros/${encodeURIComponent(params.solicitacaoId)}/resposta`
    const resposta = await this.http.post<WireRespostaDeParametros>(url, { valores: params.valores })
    return {
      sessionId: resposta.data.sessao_id,
      interactionId: resposta.data.interacao_retomada_id,
      estado: resposta.data.estado
    }
  }

  async responderOperacaoDaTela (params: {
    sessionId: string
    interactionId: string
    pedidoId: string
    resposta: import('../dominio/operacoesDaTela').RespostaDaTela
  }): Promise<void> {
    await this.http.post<unknown>(
      `${PREFIXO_V2}/sessoes/${encodeURIComponent(params.sessionId)}/interacoes/${encodeURIComponent(params.interactionId)}/operacoes-de-tela/${encodeURIComponent(params.pedidoId)}/resposta`,
      params.resposta
    )
  }

  async confirmarNavegacaoAntecipada (params: {
    sessionId: string
    interactionId: string
    acaoId: string
    realizada: boolean
    contextoDeTela?: ContextoDeTelaEnviado
  }): Promise<void> {
    const corpo: Record<string, unknown> = {
      acao_id: params.acaoId,
      realizada: params.realizada
    }
    if (params.realizada && params.contextoDeTela) corpo.contexto_de_tela = params.contextoDeTela
    await this.http.post<unknown>(
      `${PREFIXO_V2}/sessoes/${params.sessionId}/interacoes/${params.interactionId}/navegacao-antecipada/confirmacao`,
      corpo
    )
  }

  /**
   * O contrato desta rota é uma página JSON paginada por cursor
   * (`PaginaStreamAssistenteResposta`), não `text/event-stream` — por isso o
   * consumo é por poll com cursor, não por `EventSource`/`ReadableStream`.
   */
  async listarEventos (params: { sessionId: string, interactionId: string, cursor: number }): Promise<WirePaginaStream> {
    const resposta = await this.http.get<WirePaginaStream>(
      `${PREFIXO_V2}/sessoes/${params.sessionId}/interacoes/${params.interactionId}/eventos`,
      { params: { cursor: params.cursor, limite: 100 } }
    )
    return resposta.data
  }

  /**
   * Faz poll do stream de eventos até a interação terminar (evento com `tipo`
   * de resposta terminal no payload, ou `estado_interacao` deixar de ser
   * `processando`). `aoReceberEvento` recebe cada evento intermediário para
   * que a UI reflita progresso real do servidor.
   */
  /**
   * Página de histórico paginada por cursor opaco (`proximo_cursor`, `null`
   * quando não há mais páginas). Sem cursor, devolve a primeira página.
   */
  async historico (params: { cursor?: string, t: TradutorAssistente }): Promise<PaginaDeHistorico> {
    const resposta = await this.http.get<WirePaginaHistorico>(`${PREFIXO_V2}/historico`, {
      params: params.cursor ? { cursor: params.cursor } : {}
    })
    return {
      contractVersion: resposta.data.versao_contrato,
      items: resposta.data.itens.map(item => mapearItemHistorico(item, params.t)),
      nextCursor: resposta.data.proximo_cursor
    }
  }

  /**
   * E12 — as conversas que a pessoa pode RELER, paginadas por cursor.
   *
   * Leitura pura do lado do backend: nada aqui reabre, reativa ou retoma
   * conversa. O contrato do wire desce cru para a apresentação (que é quem
   * decide o que o cartão mostra, em `historico/cartaoDeConversa.ts`) porque
   * não há nada a mapear — os campos já vêm com o nome que significam.
   */
  async listarConversas (params: { cursor?: string, limite?: number } = {}): Promise<PaginaDeConversas> {
    const query: Record<string, unknown> = {}
    if (params.cursor) query.cursor = params.cursor
    if (params.limite !== undefined) query.limite = params.limite
    const resposta = await this.http.get<WirePaginaConversas>(`${PREFIXO_V2}/sessoes`, { params: query })
    return {
      contractVersion: resposta.data.versao_contrato,
      items: resposta.data.itens,
      nextCursor: resposta.data.proximo_cursor
    }
  }

  async obterTranscricao (sessaoId: string): Promise<TranscricaoDeConversa> {
    const resposta = await this.http.get<WireTranscricaoConversa>(
      `${PREFIXO_V2}/sessoes/${sessaoId}/transcricao`
    )
    return {
      id: resposta.data.id,
      estado: resposta.data.estado,
      criada_em: resposta.data.criada_em,
      fechada_em: resposta.data.fechada_em,
      turnos: resposta.data.turnos || []
    }
  }

  async aguardarConclusaoDaInteracao (
    params: { sessionId: string, interactionId: string },
    aoReceberEvento?: (evento: WireEventoStream) => unknown | Promise<unknown>,
    opcoes: OpcoesDoPoll = {}
  ): Promise<ResultadoDoPoll> {
    const intervaloFixo = opcoes.intervaloMs
    const maximoDeTentativas = opcoes.maximoDeTentativas ?? MAXIMO_DE_TENTATIVAS_DO_POLL
    const agora = opcoes.agora ?? (() => Date.now())
    let cursor = 0
    let resposta: WireRespostaAssistente | null = null
    let selecao: SelecaoDaAntecipacao | undefined
    let solicitacaoDeParametros: SolicitacaoDeParametros | undefined
    let ultimaPagina: WirePaginaStream | null = null
    /*
     * Janela estendida pela reconexão do MCP. O orçamento normal (~5 min)
     * cobre um turno; as esperas da retomada somam até ~7 min. Cada aviso
     * empurra o prazo para depois da próxima tentativa anunciada, e o poll
     * continua enquanto houver orçamento OU prazo — o que vencer por último.
     */
    let prazoEstendido = 0

    for (let tentativa = 0; ; tentativa++) {
      const pagina = await this.listarEventos({ ...params, cursor })
      ultimaPagina = pagina
      const paginaTerminal = pagina.estado_interacao !== 'processando' || pagina.eventos.some(
        evento => evento.tipo === 'erro' || typeof evento.payload?.tipo === 'string'
      )
      for (const evento of pagina.eventos) {
        if (paginaTerminal && ['navegacao_antecipada', 'operacao_de_tela'].includes(evento.tipo)) continue
        await aoReceberEvento?.(evento)
        const selecaoDoEvento = selecaoDaAntecipacaoDoEvento(evento)
        if (selecaoDoEvento) selecao = selecaoDoEvento
        const solicitacaoDoEvento = solicitacaoDeParametrosDoEvento(evento)
        if (solicitacaoDoEvento) solicitacaoDeParametros = solicitacaoDoEvento
        if (evento.tipo === EVENTO_DE_RECONEXAO_DO_MCP) {
          prazoEstendido = Math.max(prazoEstendido, prazoDaReconexaoDoMcp(evento.payload, agora()))
        }
        if (evento.tipo === 'erro') {
          const codigoReal = typeof evento.payload?.codigo === 'string' ? evento.payload.codigo : ''
          const correlacao = typeof evento.payload?.correlacao === 'string' ? evento.payload.correlacao : undefined
          resposta = {
            tipo: 'erro',
            codigo: codigoReal || 'assistente_indisponivel',
            mensagem: String(evento.payload?.mensagem || ''),
            // `escrita_incerta` chega com `repetivel: false`: repetir às cegas
            // poderia gravar duas vezes. Sem o campo, segue retentável.
            repetivel: evento.payload?.repetivel !== false,
            ...(correlacao ? { correlacao } : {})
          }
          break
        }
        if (typeof evento.payload?.tipo === 'string') {
          resposta = evento.payload as unknown as WireRespostaAssistente
          break
        }
      }
      cursor = pagina.proximo_cursor
      if (resposta || pagina.estado_interacao !== 'processando') break
      // Depois da última tentativa não há o que esperar: dormir aqui só
      // atrasaria a mensagem de desistência que já está decidida — salvo se
      // o agente avisou que está reconectando ao MCP e o prazo dele não venceu.
      const restaOrcamento = tentativa < maximoDeTentativas - 1
      if (!restaOrcamento && agora() >= prazoEstendido) break
      const espera = intervaloFixo ?? intervaloDoPoll(tentativa)
      await new Promise(resolve => globalThis.setTimeout(resolve, espera))
    }
    return {
      resposta,
      estadoInteracao: ultimaPagina?.estado_interacao || 'processando',
      revisaoSessao: ultimaPagina?.revisao_sessao ?? 0,
      ...(selecao ? { selecao } : {}),
      ...(solicitacaoDeParametros ? { solicitacaoDeParametros } : {})
    }
  }
}

// ---------------------------------------------------------------------------
// Mutações — prévia, confirmação (com digest obrigatório) e reconciliação.
// ---------------------------------------------------------------------------

export interface ResultadoDeConfirmacao {
  summary: string
  details: Array<{ label: string, value: string }>
}

/**
 * R6 — a tela afetada como o desfecho a publicou, ou `null`.
 *
 * O campo é opcional no contrato: uma capacidade sem tela declarada e um
 * desfecho que não concluiu vêm sem ele, e a ausência é informação — significa
 * "não mande recarregar nada". Por isso a leitura é estreita: qualquer forma
 * que não seja o par completo de textos, com rota local, some.
 */
export function telaAfetadaDaConfirmacao (bruta: unknown): TelaAfetadaPelaMutacao | null {
  if (typeof bruta !== 'object' || bruta === null) return null

  const { identificador, rota } = bruta as Record<string, unknown>
  if (typeof identificador !== 'string' || identificador.trim() === '') return null
  if (typeof rota !== 'string' || !rota.startsWith('/') || rota.startsWith('//')) return null

  return { identificador: identificador.trim(), rota: rota.trim() }
}

export class MutacoesAssistenteHttpClient {
  constructor (private readonly http: ClienteHttpAssistente) {}

  async preview (
    capability: CapacidadeAssistente,
    input: Record<string, string>,
    vinculo: { sessionId?: string, interactionId?: string } = {}
  ): Promise<PreviaDeMutacao> {
    const resposta = await this.http.post<WireRespostaPrevia>(`${PREFIXO_V2}/mutacoes/preview`, {
      capacidade: capability,
      entrada: input,
      sessao_id: vinculo.sessionId,
      interacao_id: vinculo.interactionId
    })
    const dados = resposta.data
    return {
      previewId: dados.preview_id,
      digest: dados.digest,
      capability: dados.capacidade,
      targets: dados.alvos.map(alvo => ({ label: alvo.label, value: alvo.value })),
      effects: dados.efeitos.map(efeito => ({
        label: efeito.label,
        value: efeito.value,
        severity: (efeito.severity as 'info' | 'warn' | 'danger' | undefined) || 'info'
      })),
      affectedCount: dados.quantidade_afetada,
      reversible: dados.reversivel,
      expiresAt: dados.expira_em,
      input: dados.entrada,
      sessionId: vinculo.sessionId,
      interactionId: vinculo.interactionId
    }
  }

  async confirm (preview: PreviaDeMutacao, idempotencyKey: string): Promise<{
    estado: string
    resultado: ResultadoDeConfirmacao
    telaAfetada: TelaAfetadaPelaMutacao | null
  }> {
    if (!preview.digest) {
      throw new Error('previa_sem_digest: confirmação recusada localmente — prévia adulterada ou obsoleta')
    }
    const resposta = await this.http.post<WireRespostaConfirmacao>(`${PREFIXO_V2}/mutacoes/confirmar`, {
      capacidade: preview.capability,
      preview_id: preview.previewId,
      digest: preview.digest,
      chave_idempotencia: idempotencyKey
    })
    return {
      estado: resposta.data.estado,
      resultado: {
        summary: resposta.data.resumo,
        details: resposta.data.detalhes.map(item => ({ label: item.label, value: item.value }))
      },
      telaAfetada: telaAfetadaDaConfirmacao(resposta.data.tela_afetada)
    }
  }

  async reconcile (idempotencyKey: string): Promise<{ estado: 'aplicado' | 'nao-aplicado' | 'incerto', resumo: string | null, details: Array<{ label: string, value: string }> }> {
    const resposta = await this.http.get<WireRespostaReconciliacao>(`${PREFIXO_V2}/mutacoes/${idempotencyKey}`)
    const estado = resposta.data.estado === 'concluida'
      ? 'aplicado'
      : resposta.data.estado === 'falha_conhecida'
        ? 'nao-aplicado'
        : 'incerto'
    return {
      estado,
      resumo: resposta.data.resumo,
      details: resposta.data.detalhes.map(item => ({ label: item.label, value: item.value }))
    }
  }

  novaChaveDeConfirmacao (): string {
    return gerarId()
  }

  expirou (preview: PreviaDeMutacao, agora = new Date()): boolean {
    return agora.getTime() > new Date(preview.expiresAt).getTime()
  }
}

// ---------------------------------------------------------------------------
// Áudio — rascunho de transcrição efêmero: grava, transcreve, o usuário revisa
// o texto no Composer e só então confirma (ou descarta sem custo de envio).
// ---------------------------------------------------------------------------

export interface RascunhoDeTranscricao {
  rascunhoId: string
  texto: string
  expiraEm: string
}

export class AudioAssistenteHttpClient {
  constructor (private readonly http: ClienteHttpAssistente) {}

  async transcrever (params: {
    sessionId: string
    audio: Blob
    duracaoMs: number
    mimeType: string
  }): Promise<RascunhoDeTranscricao> {
    const formData = new FormData()
    formData.append('audio', params.audio)
    formData.append('duracao_ms', String(Math.round(params.duracaoMs)))
    formData.append('mime_type', params.mimeType)
    const resposta = await this.http.post<WireRascunhoTranscricao>(
      `${PREFIXO_V2}/sessoes/${params.sessionId}/audios/transcricao`,
      formData
    )
    return {
      rascunhoId: resposta.data.rascunho_id,
      texto: resposta.data.texto,
      expiraEm: resposta.data.expira_em
    }
  }

  async confirmar (params: {
    sessionId: string
    rascunhoId: string
    textoRevisado: string
  }): Promise<{ texto: string }> {
    const resposta = await this.http.post<WireTranscricaoConfirmada>(
      `${PREFIXO_V2}/sessoes/${params.sessionId}/audios/${params.rascunhoId}/confirmacao`,
      { texto_revisado: params.textoRevisado }
    )
    return { texto: resposta.data.texto }
  }
}

export { erroGenerico }
